import React, { useState, useContext, useEffect, useCallback, useRef } from 'react';
import { ChessSocialContext } from '../util/ChessSocialContext';
import { useChessAnalysis } from '../util/ChessAnalysisContext'; // Add our context
import { usePremium } from '../util/PremiumFilter';
import { BLACK, Chess } from 'chess.js';
import stockfishEngine from '../util/StockfishEngine'; // Import our engine utility
import {
  evaluatePosition,
  initStockfish,
  getCacheStatus,
  checkMoveAgainstCache,
  storeCachedLines,
  setRecalculationThreshold,
  getRecalculationThreshold,
  resetCache
} from '../util/stockfish';
import Brilliant from '../images/FooterIcons/Brilliance2.png';
import Blunder from '../images/FooterIcons/Blunder2.png';
import updateGameState from '../components/ArchivedChess/Board';
import { GOLD } from '../constants/colors';

// Add animation styles for loading dots
const pulseAnimation = `
@keyframes pulse {
  0% { transform: scale(0.8); opacity: 0.5; }
  50% { transform: scale(1.2); opacity: 1; }
  100% { transform: scale(0.8); opacity: 0.5; }
}

@keyframes buttonPulse {
  0% { transform: scale(1); box-shadow: 0 0 0 0 rgba(255, 0, 0, 0.7); }
  70% { transform: scale(1.02); box-shadow: 0 0 0 6px rgba(255, 0, 0, 0); }
  100% { transform: scale(1); box-shadow: 0 0 0 0 rgba(255, 0, 0, 0); }
}
`;

// Sample PV data (Principal Variations) for starting position
const SAMPLE_PVS = [
  {
    line: 1,
    evaluation: "+0.08",
    bestMove: "e2e4", // e4 opening
    moveName: "e4",
    fullLine: "e4 c5 Nf3 e6 Nc3 Nc6 d4 cxd4 Nxd4 Nf6 Be2 Bb4 Nxc6 bxc6 O-O Bxc3 bxc3",
    moves: ["e4", "c5", "Nf3", "e6", "Nc3", "Nc6", "d4", "cxd4", "Nxd4", "Nf6", "Be2", "Bb4", "Nxc6", "bxc6", "O-O", "Bxc3", "bxc3"]
  },
  {
    line: 2,
    evaluation: "+0.08",
    bestMove: "d2d4", // d4 opening
    moveName: "d4",
    fullLine: "d4 d5 c4 e6 Nf3 Nf6 Nc3 c5 cxd5 exd5 Bg5 Be7 e3 Nc6 dxc5 O-O Bd3",
    moves: ["d4", "d5", "c4", "e6", "Nf3", "Nf6", "Nc3", "c5", "cxd5", "exd5", "Bg5", "Be7", "e3", "Nc6", "dxc5", "O-O", "Bd3"]
  },
  {
    line: 3,
    evaluation: "+0.08",
    bestMove: "g1f3", // Nf3 opening
    moveName: "Nf3",
    fullLine: "Nf3 Nf6 d4 e6 c4 d5 Nc3 c5 cxd5 exd5 Bg5 Be7 e3 Nc6 dxc5 O-O Bd3",
    moves: ["Nf3", "Nf6", "d4", "e6", "c4", "d5", "Nc3", "c5", "cxd5", "exd5", "Bg5", "Be7", "e3", "Nc6", "dxc5", "O-O", "Bd3"]
  }
];

// Near the top of the file, replace the simple global variable with a more detailed structure
// Global variable to store detailed classification information
window.moveClassificationData = {
  classification: null,      // The classification result (good, inaccuracy, blunder, etc.)
  positionFEN: null,         // The FEN of the position that was classified
  timestamp: null            // When the classification occurred
};

const Stockfish = ({ isViewGame }) => {
  // Define colors at the top of the component
  const colors = {
    primary: '#1a2f16',    // Darker forest green
    secondary: '#2a4020',  // Darker medium forest green
    tertiary: '#3a5228',   // Darker light forest green
    background: '#1a2635', // Keep existing dark background
  };

  const {
    showStockfish,
    setShowStockfish,
    setEvaluation: setGlobalEvaluation,
    stockfishArrows,
    setStockfishArrows,
    currentFEN, // Get the current FEN from context
    isPuzzleMode // +++ Get isPuzzleMode from context +++
  } = useContext(ChessSocialContext);

  // New state for arrow setting dropdown
  const [arrowSetting, setArrowSetting] = useState('NONE'); // 'NONE', 'BEST', 'ALL'
  const [hoveredMove, setHoveredMove] = useState(null); // Track currently hovered move
  const [currentArrow, setCurrentArrow] = useState(null); // Track current best move arrow
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [activeTab, setActiveTab] = useState('main');
  const [uiVersion, setUiVersion] = useState('1'); // Default to Version 1
  const [activeLine, setActiveLine] = useState(1); // Default to showing line 1 (for Version 2)
  const [expandedLine, setExpandedLine] = useState(null); // For Version 1, which line is expanded
  const [currentMoveIndex, setCurrentMoveIndex] = useState(-1); // Track the current move index in the displayed line
  const [evaluation, setEvaluation] = useState(0); // State to store the current evaluation
  const [isEvaluating, setIsEvaluating] = useState(false); // To track if evaluation is in progress
  // Add state for real Stockfish analysis results
  const [analysisResults, setAnalysisResults] = useState(null);
  const [convertedPVs, setConvertedPVs] = useState([]); // Store converted PVs (LAN to SAN)

  // Add state to track previous position and evaluation for move classification
  const [previousPositionFEN, setPreviousPositionFEN] = useState(null);
  const [previousEvaluation, setPreviousEvaluation] = useState(null);

  // +++ Add state for the starting turn of the analysis +++
  const [analysisStartTurn, setAnalysisStartTurn] = useState('w'); // Default to White

  // Track the line number of the currently hovered move
  const [hoveredMoveLineNumber, setHoveredMoveLineNumber] = useState(1);

  // New intelligent caching state variables
  const [isFollowingCachedLine, setIsFollowingCachedLine] = useState(false); // Whether we're following a cached line
  const [followingLineIndex, setFollowingLineIndex] = useState(-1); // Which line we're following (0, 1, 2)
  const [movesMade, setMovesMade] = useState(0); // How many moves we've made following the cached line
  const [recalculationThreshold, setRecalcThreshold] = useState(6); // Default recalculation threshold set to 6
  const [analysisDepth, setAnalysisDepth] = useState(15); // Default analysis depth set to 15
  const [recalculateButtonColor, setRecalculateButtonColor] = useState('#1a2635'); // Default button color
  const [recalculationPercent, setRecalculationPercent] = useState(0); // Progress toward recalculation (0-100%)
  const [useCachedEvaluation, setUseCachedEvaluation] = useState(false); // Whether we're using a cached evaluation
  const [lastPlayedMove, setLastPlayedMove] = useState(null); // Track the last move played
  // Add error state to display engine errors
  const [analysisError, setAnalysisError] = useState(null);

  // New state for tracking move history in Modern UI
  const [moveHistory, setMoveHistory] = useState([]); // Array of {lineIndex, moveIndex} objects to track moves made
  const [selectedLineIndex, setSelectedLineIndex] = useState(null); // The line currently being followed in Modern UI
  const [currentMoveDisplayIndex, setCurrentMoveDisplayIndex] = useState(0); // Index of move to display in the buttons (0 = first move)

  // +++ State for Full Game Analysis +++
  const [isFullAnalysisRunning, setIsFullAnalysisRunning] = useState(false);
  const [fullAnalysisProgress, setFullAnalysisProgress] = useState(0); // Progress percentage (0-100)
  const [fullAnalysisCurrentMove, setFullAnalysisCurrentMove] = useState(0); // Which move is being analyzed

  // +++ Ref to track if initial puzzle setup is done +++
  const puzzleSetupDoneRef = useRef(false);

  // Track the current board FEN and last board move index
  const [currentBoardFEN, setCurrentBoardFEN] = useState('');
  const [currentBoardMoveIndex, setCurrentBoardMoveIndex] = useState(-1);
  // Add state to store the original analysis position
  const [originalAnalysisFEN, setOriginalAnalysisFEN] = useState('');
  const [hasManuallyAnalyzed, setHasManuallyAnalyzed] = useState(false);

  // Add this near the other state declarations
  const lastAnalyzedPositionRef = useRef(null);

  // We still get premium context in case we need it later
  const { isPremiumUser, premiumFeatures } = usePremium();

  // Use our chess analysis context
  const {
    currentPosition,
    setCurrentPosition,
    isAnalyzing: contextIsAnalyzing,
    setIsAnalyzing: setContextIsAnalyzing,
    analysisResults: contextResults,
    setAnalysisResults: setContextResults,
    engineReady,
    setEngineReady,
    shouldRefreshUI,
    setShouldRefreshUI,
    updatePositionSilently,
    positionsWithAnalysis
  } = useChessAnalysis();

  // --- Add State for Last Board Move --- 
  const [lastBoardMoveSAN, setLastBoardMoveSAN] = useState(null);

  // Function to get the board element - moved up to avoid initialization issues
  const getBoardElement = useCallback(() => {
    const boardEl = document.querySelector('.chess-board');
    return boardEl;
  }, []);

  // Function to get the current board FEN - moved up to avoid initialization issues
  const getCurrentFEN = useCallback(() => {
    // Use the position from the chess analysis context first if available
    if (currentPosition) {
      return currentPosition;
    }

    // Then try the FEN from the global context
    if (currentFEN) {
      return currentFEN;
    }

    // Fallback to checking for window.getCurrentBoardPosition
    if (typeof window.getCurrentBoardPosition === 'function') {
      const fen = window.getCurrentBoardPosition();
      return fen;
    }

    // Final fallback to the DOM
    const boardEl = getBoardElement();
    return boardEl?.getAttribute('data-fen') || 'rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1';
  }, [currentPosition, currentFEN, getBoardElement]);

  // Add a function to reset classification data
  const resetClassificationData = () => {
    window.moveClassificationData = {
      classification: null,
      positionFEN: null,
      timestamp: null
    };
  }; // May not be required here...

  // Function to get cached analysis for a position
  const getCachedAnalysis = (fen) => {
    try {
      // Check if we're in AddGame.js which would have set up the cache
      const cacheExists = window.moveClassificationData &&
        window.currentAnalysisCache &&
        window.currentAnalysisCache.pvs;

      // Log cache check
      // console.log('Stockfish.js: Checking cache for position', {
      //   position: fen,
      //   cacheExists: cacheExists,
      //   matchesCurrentPosition: window.moveClassificationData?.positionFEN === fen
      // });

      if (cacheExists) {
        // Return the cached analysis data
        return window.currentAnalysisCache;
      }

      return null;
    } catch (error) {
      console.error("Error checking for cached analysis:", error);
      return null;
    }
  };

  // Add a function to send completed analysis to AddGame.js
  const sendAnalysisToAddGame = (analysisData) => {
    try {
      // If we have a global function to receive analysis data (set by AddGame.js)
      if (typeof window.receiveStockfishAnalysis === 'function') {
        // Get the current position
        const currentFEN = getCurrentFEN() || currentPosition;

        // Add completion flag to indicate full-depth analysis
        const completeAnalysisData = {
          ...analysisData,
          isComplete: true,
          positionFEN: currentFEN,  // Store the position FEN for later matching
          depth: analysisDepth      // Add the depth at which analysis was performed
        };

        // Send the data to AddGame.js
        // This will update the move.analysis property in the moves array
        // via the saveAnalysisToMove function in AddGame.js
        window.receiveStockfishAnalysis(completeAnalysisData);
      }
    } catch (error) {
      console.error("Error sending analysis to AddGame.js:", error);
    }
  };

  // Update the handleAnalyze function to be async
  const handleAnalyze = async (forceCalculation = false) => {
    // Get the current position first
    const currentFEN = getCurrentFEN() || currentPosition;

    // Get the current move index from the window global if available
    const currentMoveIdx = typeof window.getCurrentMoveIndex === 'function' ?
      window.getCurrentMoveIndex() : null;

    // console.log('Stockfish.js: handleAnalyze called', {
    //   forceCalculation,
    //   position: currentFEN,
    //   currentMoveIdx,
    // });

    // First check for analysis data in the NEXT move (N+1), since that contains
    // the analysis for the position after the current move
    let nextMoveHasAnalysis = false;
    if (!forceCalculation && currentMoveIdx !== null && typeof window.getMove === 'function' && window.moves) {
      try {
        // Calculate the next move index (N+1)
        const nextMoveIdx = parseInt(currentMoveIdx) + 1;

        // Get the next move object if it exists
        if (nextMoveIdx < window.moves.length) {
          const nextMoveData = window.getMove(window.moves, nextMoveIdx.toString());

          if (nextMoveData && nextMoveData.analysis) {
            

            // Get position FEN from the analysis if available
            const analysisFEN = nextMoveData.analysis.positionFEN || currentFEN;

            // Compare FENs but ignore the move counters at the end (last 2 fields)
            // This ensures we're looking at the same board position
            const currentFENBase = currentFEN.split(' ').slice(0, 4).join(' ');
            const analysisFENBase = analysisFEN.split(' ').slice(0, 4).join(' ');

            // console.log('Stockfish.js: Comparing N+1 analysis position with current', {
            //   currentFENBase,
            //   analysisFENBase,
            //   isMatch: currentFENBase === analysisFENBase
            // });

            // Only use the analysis if it's for the same position
            if (currentFENBase === analysisFENBase) {
              // Use the cached data from N+1 move.analysis
              nextMoveHasAnalysis = true;

              // Update UI state to show we're analyzing
              setIsAnalyzing(true);
              setContextIsAnalyzing(true);

              // Update the window cache objects from the move data
              window.currentAnalysisCache = nextMoveData.analysis;

              // Update classification data if available
              if (nextMoveData.analysis.classification) {
                window.moveClassificationData = {
                  classification: nextMoveData.analysis.classification,
                  positionFEN: currentFEN,
                  timestamp: Date.now()
                };
              }

              // Update the PVs with cached data
              if (nextMoveData.analysis.pvs && nextMoveData.analysis.pvs.length > 0) {
                setConvertedPVs(nextMoveData.analysis.pvs);
              }

              // Update evaluation if available
              if (nextMoveData.analysis.scores && nextMoveData.analysis.scores.length > 0) {
                setEvaluation(nextMoveData.analysis.scores[0]);
                if (setGlobalEvaluation) {
                  setGlobalEvaluation(nextMoveData.analysis.scores[0]);
                }
              }

              // Set state to show we have analysis without evaluating
              setIsEvaluating(false);
              setShouldRefreshUI(false);

              // Update window data to mark this position as analyzed
              window.moveClassificationData = {
                classification: nextMoveData.analysis.classification || null,
                positionFEN: currentFEN,
                timestamp: Date.now()
              };

              return;
            } else {
              
            }
          }
        }

        // If we couldn't find N+1 data, try the current move as fallback
        const moveData = window.getMove(window.moves, currentMoveIdx);

        if (moveData && moveData.analysis) {
          

          // Get position FEN from the analysis if available
          const analysisFEN = moveData.analysis.positionFEN || currentFEN;

          // Compare FENs but ignore the move counters at the end
          const currentFENBase = currentFEN.split(' ').slice(0, 4).join(' ');
          const analysisFENBase = analysisFEN.split(' ').slice(0, 4).join(' ');

          // console.log('Stockfish.js: Comparing current move analysis position with current', {
          //   currentFENBase,
          //   analysisFENBase,
          //   isMatch: currentFENBase === analysisFENBase
          // });

          // Only use the analysis if it's for the same position
          if (currentFENBase === analysisFENBase) {
            // Use the cached data from move.analysis
            nextMoveHasAnalysis = true;

            // Update UI state to show we're analyzing
            setIsAnalyzing(true);
            setContextIsAnalyzing(true);

            // Update the window cache objects from the move data
            window.currentAnalysisCache = moveData.analysis;

            // Update classification data if available
            if (moveData.analysis.classification) {
              window.moveClassificationData = {
                classification: moveData.analysis.classification,
                positionFEN: currentFEN,
                timestamp: Date.now()
              };
            }

            // Update the PVs with cached data
            if (moveData.analysis.pvs && moveData.analysis.pvs.length > 0) {
              setConvertedPVs(moveData.analysis.pvs);
            }

            // Update evaluation if available
            if (moveData.analysis.scores && moveData.analysis.scores.length > 0) {
              setEvaluation(moveData.analysis.scores[0]);
              if (setGlobalEvaluation) {
                setGlobalEvaluation(moveData.analysis.scores[0]);
              }
            }

            // Set state to show we have analysis without evaluating
            setIsEvaluating(false);
            setShouldRefreshUI(false);

            // Update window data to mark this position as analyzed
            window.moveClassificationData = {
              classification: moveData.analysis.classification || null,
              positionFEN: currentFEN,
              timestamp: Date.now()
            };

            return;
          } else {
            
          }
        }
      } catch (error) {
        console.error('Stockfish.js: Error checking moves for analysis data:', error);
      }
    }

    // If we didn't find analysis in the moves, check our secondary sources
    // Check both our position mapping and window data
    const hasExistingData = !forceCalculation && (
      positionsWithAnalysis[currentFEN] ||
      window.moveClassificationData?.positionFEN === currentFEN
    );

    // Log cache status
    // console.log('Stockfish.js: Cache check', {
    //   nextMoveHasAnalysis,
    //   inPositionsMap: positionsWithAnalysis[currentFEN] ? true : false,
    //   inWindowData: window.moveClassificationData?.positionFEN === currentFEN,
    //   hasExistingData
    // });

    // Skip analysis if we already have data for this position (unless force calculation is true)
    if (hasExistingData) {
      

      // Still update the UI to show we have data
      setIsAnalyzing(true);
      setContextIsAnalyzing(true);

      // If we have cached PVs, use them
      if (window.currentAnalysisCache?.pvs) {
        setConvertedPVs(window.currentAnalysisCache.pvs);
      }

      // If we have cached evaluation, use it
      if (window.currentAnalysisCache?.scores && window.currentAnalysisCache.scores.length > 0) {
        setEvaluation(window.currentAnalysisCache.scores[0]);
        if (setGlobalEvaluation) {
          setGlobalEvaluation(window.currentAnalysisCache.scores[0]);
        }
      }

      // Set state to show we have analysis without evaluating
      setIsEvaluating(false);
      setShouldRefreshUI(false);
      return;
    }

    // At this point, we need to do a fresh analysis
    // Reset classification data ONLY when we're starting a new analysis
    resetClassificationData();

    setIsAnalyzing(true);
    setContextIsAnalyzing(true);
    setShouldRefreshUI(true);
    setActiveLine(1);
    setPreviousPositionFEN(null);
    setPreviousEvaluation(null);

    // Check if we have cached analysis for this position
    const cachedAnalysis = getCachedAnalysis(currentFEN);

    if (cachedAnalysis && !forceCalculation) {
      // Use the cached data
      if (cachedAnalysis.pvs && cachedAnalysis.pvs.length > 0) {
        // Update the PVs with cached data
        setConvertedPVs(cachedAnalysis.pvs);

        // Update evaluation if available
        if (cachedAnalysis.scores && cachedAnalysis.scores.length > 0) {
          setEvaluation(cachedAnalysis.scores[0]);
          if (setGlobalEvaluation) {
            setGlobalEvaluation(cachedAnalysis.scores[0]);
          }
        }

        // Update classification information
        if (cachedAnalysis.classification) {
          window.moveClassificationData = {
            classification: cachedAnalysis.classification,
            positionFEN: currentFEN,
            timestamp: Date.now()
          };
        }

        // Update state to indicate analysis is complete
        setIsEvaluating(false);
        setShouldRefreshUI(false);

        return;
      }
    }

    // No cache or forced recalculation, proceed with normal analysis

    // Clear any previous errors
    setAnalysisError(null);
    // ---> Clear arrows on new analysis <---
    setStockfishArrows([]);

    // PRIORITY ORDER FOR POSITION:
    // 1. If originalAnalysisFEN is set, use that (it's the starting position specifically for analysis)
    // 2. Otherwise, use getCurrentFEN() which gets the position from the board
    const analysisPosition = originalAnalysisFEN || currentFEN;

    if (!analysisPosition) {
      console.error('Could not get current position for analysis');
      setAnalysisError('Could not determine the current board position');
      setIsEvaluating(false);
      setShouldRefreshUI(false); // Stop UI refresh on error
      return;
    }

    // Update the original analysis position
    setOriginalAnalysisFEN(analysisPosition);

    // Reset the evaluation in case of new position analysis
    if (!useCachedEvaluation || forceCalculation) {
      setEvaluation(null);
      if (setGlobalEvaluation) {
        setGlobalEvaluation(null);
      }
    }

    setIsEvaluating(true);
    setCurrentMoveIndex(-1); // Reset the current move index
    setLastBoardMoveSAN(null); // <-- Reset last move display

    // Reset cache-related state
    setIsFollowingCachedLine(false);
    setFollowingLineIndex(-1);
    setMovesMade(0);
    setUseCachedEvaluation(false);

    // Set the flag that user has manually analyzed
    setHasManuallyAnalyzed(true);

    // Always show line 1 (top engine line) when starting analysis
    setActiveLine(1);
    setPreviousPositionFEN(null);
    setPreviousEvaluation(null);

    try {
      // Only proceed if the engine is ready
      if (!engineReady) {
        console.error('Stockfish engine not ready');
        setAnalysisError('Engine not ready. Please wait or refresh the page.');
        setIsEvaluating(false);
        setShouldRefreshUI(false); // Stop UI refresh on error
        return;
      }

      // Stop any ongoing calculations
      await stockfishEngine.stop();

      // Analyze the position using our StockfishEngine utility
      await stockfishEngine.analyzePosition(analysisPosition, {
        depth: analysisDepth,
        multipv: 3
      });

      // The results will be processed through the message listener we set up
    } catch (error) {
      console.error('Error starting analysis:', error);
      setIsEvaluating(false);
      setShouldRefreshUI(false); // Stop UI refresh on error
      setAnalysisError(`Analysis error: ${error.message || 'Unknown error'}`);
    }
  };

  // Add animation styles for the loading dots
  useEffect(() => {
    const styleEl = document.createElement('style');
    styleEl.innerHTML = pulseAnimation;
    document.head.appendChild(styleEl);
    return () => {
      document.head.removeChild(styleEl);
    };
  }, []);

  // Load settings from localStorage on component mount
  useEffect(() => {
    // Load UI version preference
    const savedVersion = localStorage.getItem('stockfishUiVersion');
    if (savedVersion) {
      setUiVersion(savedVersion);
    }

    // Load arrow setting preference
    const savedArrowSetting = localStorage.getItem('stockfishArrowSetting');
    if (savedArrowSetting && ['NONE', 'BEST', 'ALL'].includes(savedArrowSetting)) {
      setArrowSetting(savedArrowSetting);
    } else {
      setArrowSetting('NONE'); // Default to NONE if invalid or not found
    }

    // Load analysis depth preference
    const savedDepth = localStorage.getItem('stockfishAnalysisDepth');
    if (savedDepth !== null) {
      setAnalysisDepth(parseInt(savedDepth));
    }

    // Load recalculation threshold preference
    const savedThreshold = localStorage.getItem('stockfishRecalcThreshold');
    if (savedThreshold !== null) {
      const thresholdValue = parseInt(savedThreshold);
      setRecalcThreshold(thresholdValue);
      setRecalculationThreshold(thresholdValue);
    }
  }, []); // Removed setGlobalShowArrows dependency

  // Save UI version preference to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('stockfishUiVersion', uiVersion);
  }, [uiVersion]);

  // Save arrow setting preference to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('stockfishArrowSetting', arrowSetting);
  }, [arrowSetting]);

  // Save analysis depth preference to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('stockfishAnalysisDepth', analysisDepth.toString());
  }, [analysisDepth]);

  // Save recalculation threshold preference to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('stockfishRecalcThreshold', recalculationThreshold.toString());
  }, [recalculationThreshold]);

  // Convert LAN moves to SAN for display
  const convertLANtoSAN = useCallback((moves, startFen) => {
    const chess = new Chess(startFen);
    const sanMoves = [];

    for (const lanMove of moves) {
      try {
        // Make the move in the UCI/LAN format
        const move = chess.move({
          from: lanMove.substring(0, 2),
          to: lanMove.substring(2, 4),
          promotion: lanMove.length > 4 ? lanMove[4] : undefined
        });

        // If move is successful, add the SAN notation to our list
        if (move) {
          sanMoves.push(move.san);
        }
      } catch (error) {
        console.error(`Error converting move ${lanMove}:`, error);
        // Continue with the next move even if this one fails
      }
    }

    return sanMoves;
  }, []);

  // Process and prepare PV lines for display
  const processPVLines = useCallback((results, fen) => {
    if (!results || results.length === 0) return [];

    const processedLines = [];

    for (let i = 0; i < results.length; i++) {
      const result = results[i];
      if (!result) continue;

      // Convert LAN moves to SAN
      const sanMoves = convertLANtoSAN(result.pv, fen);

      // Only add to processedLines if we have valid SAN moves
      if (sanMoves.length > 0) {
        processedLines.push({
          line: i + 1, // 1-indexed for display
          evaluation: typeof result.evaluation === 'string'
            ? result.evaluation
            : result.evaluation > 0 ? `+${result.evaluation.toFixed(2)}` : result.evaluation.toFixed(2),
          bestMove: result.bestMove,
          moveName: sanMoves[0], // Only use when we have valid SAN
          fullLine: sanMoves.join(' '),
          moves: sanMoves
        });
      }
    }

    // Expose the PV lines to the window object for Board component to access
    window.stockfishPVs = processedLines;

    return processedLines;
  }, [convertLANtoSAN]);

  // Add component mount tracking to prevent multiple initializations
  const hasInitializedRef = useRef(false);

  // Effect to initialize the Stockfish engine when component mounts
  useEffect(() => {
    // Prevent multiple initializations - only run once per component lifecycle
    if (hasInitializedRef.current) {
      
      return;
    }

    // Mark as initialized
    hasInitializedRef.current = true;

    

    // Reset classification data on initialization
    resetClassificationData();

    // Initialize the engine
    stockfishEngine.init()
      .then(() => {
        setEngineReady(true);
        

        // Always set analyzing state to true to show the UI
        setIsAnalyzing(true);
        setContextIsAnalyzing(true);

        // Always set hasManuallyAnalyzed to true to enable auto-calculation
        setHasManuallyAnalyzed(true);

        // Always start analysis immediately after engine is ready
        if (currentPosition) {
          // Call handleAnalyze directly to force analysis to start
          handleAnalyze(false);
        }
      })
      .catch(error => {
        console.error('Failed to initialize Stockfish engine:', error);
        // Display an error in the UI instead of failing silently
        setIsEvaluating(false);
        setShouldRefreshUI(false); // Ensure UI doesn't show loading on error
        // Show error to user by setting some visible state
        setAnalysisError(`Failed to initialize engine: ${error.message || 'Unknown error'}`);
      });

    // Clean up the engine when component unmounts
    return () => {
      if (stockfishEngine) {
        stockfishEngine.destroy();
      }
      // Reset initialization flag on unmount
      hasInitializedRef.current = false;
    };
  }, []); // Empty dependency array - only run once on component mount

  // Effect to listen for analysis requests from 3Board.jsx
  useEffect(() => {
    if (!engineReady) return;
    
    const handleAnalysisRequest = (event) => {
      if (event.detail && event.detail.fen) {
        
        
        // Set current position and trigger analysis
        if (setCurrentPosition) {
          setCurrentPosition(event.detail.fen);
        }
        
        // Force calculation if requested
        const forceCalc = event.detail.forceCalculation || false;
        handleAnalyze(forceCalc);
      }
    };
    
    // Listen for direct stockfish-analyze-position events from 2AddGame.js
    const handleStockfishAnalyze = (event) => {
      if (event.detail && event.detail.fen) {
        
        
        // Set current position and trigger analysis
        if (setCurrentPosition) {
          setCurrentPosition(event.detail.fen);
        }
        
        // Always force calculation for direct requests
        handleAnalyze(true);
      }
    };

    // Add event listeners
    window.addEventListener("request-stockfish-analysis", handleAnalysisRequest);
    window.addEventListener("stockfish-analyze-position", handleStockfishAnalyze);

    // Clean up event listeners
    return () => {
      window.removeEventListener("request-stockfish-analysis", handleAnalysisRequest);
      window.removeEventListener("stockfish-analyze-position", handleStockfishAnalyze);
    };
  }, [engineReady, setCurrentPosition]);

  // Effect to register a message handler for engine output
  useEffect(() => {
    if (!engineReady) return;

    // Register message handler
    const removeListener = stockfishEngine.onMessage(message => {
      // Here you can process the engine messages for display

      // Parse info messages for evaluations and principal variations
      if (message.includes('info depth') && message.includes('multipv')) {
        try {
          // Extract multipv number to identify the line
          const multipvMatch = message.match(/multipv (\d+)/);
          const lineNumber = multipvMatch ? parseInt(multipvMatch[1]) : 1;

          // Extract depth
          const depthMatch = message.match(/depth (\d+)/);
          const depth = depthMatch ? parseInt(depthMatch[1]) : 0;

          // Get the current position to determine whose turn it is
          const currentFEN = getCurrentFEN() || currentPosition;
          const isBlackToMove = currentFEN && currentFEN.split(' ')[1] === 'b';

          // Only process complete lines that have reached target depth
          if (depth === analysisDepth) {
            // Extract evaluation
            let evaluation;
            if (message.includes('score cp')) {
              const cpMatch = message.match(/score cp (-?\d+)/);
              if (cpMatch) {
                // Parse the score and convert to pawn units
                let cpScore = parseInt(cpMatch[1]) / 100; // Convert centipawns to pawns

                // IMPORTANT: If it's Black's turn, negate the score to get White's perspective
                // Stockfish reports scores from the current player's perspective
                if (isBlackToMove) {
                  cpScore = -cpScore;
                }

                // Format the evaluation string with proper sign
                evaluation = cpScore >= 0 ? `+${cpScore.toFixed(2)}` : cpScore.toFixed(2);

                // Update evaluation if this is the top line
                if (lineNumber === 1) {
                  // Save the previous evaluation before updating
                  const currentPositionFEN = getCurrentFEN() || currentPosition;

                  // Only track previous evaluation if this is a direct evaluation (not from cache)
                  if (!useCachedEvaluation) {
                    setPreviousPositionFEN(currentPositionFEN);
                    setPreviousEvaluation(evaluation);
                  }

                  setEvaluation(cpScore);
                  if (setGlobalEvaluation) {
                    setGlobalEvaluation(cpScore);
                  }

                  // Calculate classification if possible and only when we reach the full analysis depth
                  if (depth >= analysisDepth) {
                    const sideToMove = currentPositionFEN.split(' ')[1];
                    const classification = calculateMoveClassification(previousEvaluation, cpScore, sideToMove, previousPositionFEN, currentPositionFEN);

                    // Log the classification (for testing)
                    if (classification) {

                      // console.log(`Classification at depth ${depth}/${analysisDepth}`, {
                      //   previousFEN: previousPositionFEN,
                      //   currentFEN: currentPositionFEN,
                      //   previousEval: previousEvaluation,
                      //   currentEval: cpScore,
                      //   sideToMove,
                      //   classification
                      // });
                    }
                    else {

                    }
                  }
                }
              }
            } else if (message.includes('score mate')) {
              const mateMatch = message.match(/score mate (-?\d+)/);
              if (mateMatch) {
                // Parse the mate score
                let mateIn = parseInt(mateMatch[1]);

                // IMPORTANT: If it's Black's turn, negate the mate score to get White's perspective
                // Stockfish reports mates from the current player's perspective
                if (isBlackToMove) {
                  mateIn = -mateIn;
                }

                evaluation = `M${mateIn}`;

                // Update evaluation if this is the top line
                if (lineNumber === 1) {
                  // Save the previous evaluation before updating
                  const currentPositionFEN = getCurrentFEN() || currentPosition;

                  // Only track previous evaluation if this is a direct evaluation (not from cache)
                  if (!useCachedEvaluation) {
                    setPreviousPositionFEN(currentPositionFEN);
                    setPreviousEvaluation(evaluation);
                  }

                  setEvaluation(evaluation);
                  if (setGlobalEvaluation) {
                    setGlobalEvaluation(evaluation);
                  }

                  // Calculate classification if possible and only when we reach the full analysis depth
                  if (depth >= analysisDepth) {
                    const sideToMove = currentPositionFEN.split(' ')[1];
                    const classification = calculateMoveClassification(previousEvaluation, evaluation, sideToMove, previousPositionFEN, currentPositionFEN);

                    // Log the classification (for testing)
                    // if (classification) {
                    //   ;
                    // }
                    // else {
                    // }
                  }
                }
              }
            }

            // Extract PV string
            const pvKeywordIndex = message.indexOf(' pv ');
            if (pvKeywordIndex !== -1) {
              // Get the substring starting after " pv "
              const pvString = message.substring(pvKeywordIndex + 4).trim();
              const pvMoves = pvString.split(' ');
              const bestMove = pvMoves[0]; // First move in the *actual* PV sequence



              // Create or update this PV line in our data structure
              const startFen = originalAnalysisFEN || getCurrentFEN();

              if (startFen && bestMove && bestMove.length >= 4) { // Add validation for bestMove
                // Convert the moves to SAN notation
                const sanMoves = convertLANtoSAN(pvMoves, startFen);

                // Update our PVs state
                setConvertedPVs(prevPVs => {
                  const newPVs = [...prevPVs];
                  const lineIndex = lineNumber - 1;
                  newPVs[lineIndex] = {
                    line: lineNumber,
                    evaluation: evaluation,
                    bestMove: bestMove, // Assign the corrected bestMove
                    moveName: sanMoves[0] || "",
                    lanMoves: pvMoves, // <-- Store the full LAN sequence
                    fullLine: sanMoves.join(' '),
                    moves: sanMoves,
                    depth: depth
                  };
                  return newPVs;
                });
              } else {
                console.warn(`[Stockfish.js] Engine Message Handler: Invalid bestMove extracted ('${bestMove}') or missing startFen. Skipping PV update for line ${lineNumber}.`);
              }
            }
          }
        } catch (error) {
          console.error('Error parsing engine output:', error);
        }
      }

      // Parse best move when analysis is complete
      if (message.includes('bestmove')) {
        // Reset evaluation state when analysis is complete
        setIsEvaluating(false);
        setShouldRefreshUI(false); // Also reset UI refresh flag when analysis completes

        // Send completed analysis data to AddGame.js
        const currentFEN = getCurrentFEN() || currentPosition;

        // Save the current content states before sending analysis
        const currentMoveContent = window.moveContent;
        const currentVariationContent = window.variationContent;

        // Only send if we have valid data
        if (convertedPVs && convertedPVs.length > 0) {
          // Collect the analysis data
          const analysisData = {
            pvs: convertedPVs.map(pv => ({
              bestMove: pv.bestMove,
              evaluation: pv.evaluation,
              moveName: pv.moveName,
              moves: pv.moves,
              line: pv.line
            })),
            scores: convertedPVs.map(pv => pv.evaluation),
            classification: window.moveClassificationData?.classification || null,
            positionFEN: currentFEN,
            depth: analysisDepth  // Include the current analysis depth
          };

          // Send the complete analysis to AddGame.js
          sendAnalysisToAddGame(analysisData);
        }

        // Reset move sequence tracking variables
        setCurrentMoveDisplayIndex(0);
        setMovesMade(0);
        setRecalculationPercent(0);

        // Don't reset currentMoveIndex here to preserve variation content
        // setCurrentMoveIndex(-1); // This line is removed

        // Restore the content states after analysis is complete
        if (currentMoveContent !== undefined) {
          window.moveContent = currentMoveContent;
        }
        if (currentVariationContent !== undefined) {
          window.variationContent = currentVariationContent;
        }

        // Log the FEN that this analysis was based on.
        const finalAnalysisFEN = originalAnalysisFEN || getCurrentFEN(); // Use original first, fallback just in case

        // +++ Determine and set the starting turn from the FEN +++
        try {
          const turn = finalAnalysisFEN.split(' ')[1];
          if (turn === 'w' || turn === 'b') {
            setAnalysisStartTurn(turn);
          } else {
            console.warn(`Could not determine turn from FEN: ${finalAnalysisFEN}. Defaulting to 'w'.`);
            setAnalysisStartTurn('w');
          }
        } catch (fenError) {
          console.error(`Error parsing FEN (${finalAnalysisFEN}) for turn:`, fenError);
          setAnalysisStartTurn('w'); // Default on error
        }
        // +++ End setting start turn +++

        // Extract the best move
        const match = message.match(/bestmove ([a-h][1-8][a-h][1-8][qbnr]?)/);
        if (match) {
          const bestMove = match[1];

          // Ensure we've processed the PVs for the UI
          if (convertedPVs.length > 0) {
            // Force UI to update by setting activeTab
            setActiveTab('main');

            // Set analysis flag to true to show results
            if (!isAnalyzing) {
              setIsAnalyzing(true);
              setContextIsAnalyzing(true);
            }
          }
        }
      }
    });

    // Clean up listener when component unmounts or when engine status changes
    return () => removeListener();
  }, [engineReady, setGlobalEvaluation, convertLANtoSAN, analysisDepth, getCurrentFEN, originalAnalysisFEN, isAnalyzing, setContextIsAnalyzing, convertedPVs.length, setShouldRefreshUI]);

  // Monitor board for move changes
  useEffect(() => {
    // Function to handle when a move is made on the board
    const handleBoardMove = () => {
      if (!isAnalyzing || !convertedPVs || convertedPVs.length === 0) return;

      // Get the current FEN and create a chess instance
      const fen = getCurrentFEN();
      if (!fen) return;

      // ---> Clear arrows immediately on board update <---
      setStockfishArrows([]);


      // Get the last move by comparing with our last known position
      try {
        const chess = new Chess(fen);
        const history = chess.history();
        if (history.length === 0) {
          setLastBoardMoveSAN(null); // <-- Reset if history is empty
          // If lastPlayedMove is not null here, reset it too to allow re-detecting first move
          if (lastPlayedMove !== null) setLastPlayedMove(null);
          return;
        }

        const lastMove = history[history.length - 1];
        if (lastMove === lastPlayedMove) return; // Don't process the same move twice


        setLastPlayedMove(lastMove);
        setLastBoardMoveSAN(lastMove); // <-- Update last move display

        // DEBUG: Convert the board move from SAN to LAN format
        const lastMoveObject = chess.history({ verbose: true }).pop();
        const lanFormat = lastMoveObject ? `${lastMoveObject.from}${lastMoveObject.to}${lastMoveObject.promotion || ''}` : '';



        // DEBUG: Log Stockfish's best moves for comparison

        if (convertedPVs && convertedPVs.length > 0) {
          convertedPVs.forEach((pv, index) => {
            if (index < 3) { // Only log the top 3 moves



              // Compare if this board move matches any of the suggested moves
              const sanMatch = pv.moves[0] === lastMove;

              // For LAN comparison, we need the exact form from the engine
              // This is a simplified check - may need refinement
              const lanMatch = pv.bestMove && pv.bestMove.toLowerCase() === lanFormat.toLowerCase();


            }
          });

          // Overall check if move matches any of the three top lines
          const matchesAnyLine = convertedPVs.slice(0, 3).some(pv =>
            pv.moves[0] === lastMove ||
            (pv.bestMove && pv.bestMove.toLowerCase() === lanFormat.toLowerCase())
          );


        }

        // Check if this move matches any of our cached lines
        let matchedLine = -1;
        let matchedMoveIndex = -1;

        // When already following a line, check the next move in that line first
        if (isFollowingCachedLine && followingLineIndex >= 0 && movesMade < convertedPVs[followingLineIndex].moves.length) {
          const expectedMove = convertedPVs[followingLineIndex].moves[movesMade];
          if (expectedMove === lastMove) {
            matchedLine = followingLineIndex;
            matchedMoveIndex = movesMade;
          }
        }

        // If not already following a line or the move doesn't match the expected next move,
        // check the first move of all lines
        if (matchedLine === -1) {
          for (let i = 0; i < convertedPVs.length; i++) {
            if (convertedPVs[i].moves[0] === lastMove) {
              matchedLine = i;
              matchedMoveIndex = 0;
              break;
            }
          }
        }

        // Update state based on whether we have a match
        if (matchedLine >= 0) {

          setIsFollowingCachedLine(true);
          setFollowingLineIndex(matchedLine);
          setMovesMade(matchedMoveIndex + 1);

          // If we're following a specific line, highlight it in the UI
          if (uiVersion === '2') {
            setActiveLine(matchedLine + 1);
          } else if (uiVersion === '1') {
            // Optional: expand the line being followed
            // setExpandedLine(matchedLine + 1);
          }

          // Use the cached evaluation
          setUseCachedEvaluation(true);
          if (convertedPVs[matchedLine].evaluation) {
            updateEvaluationFromString(convertedPVs[matchedLine].evaluation);
          }

          // ---> Attempt to show NEXT arrow <---
          // Check arrow setting first
          if (arrowSetting !== 'NONE') {
            const nextMoveIndex = matchedMoveIndex + 1;
            const lineData = convertedPVs[matchedLine];
            if (lineData && lineData.lanMoves && nextMoveIndex < lineData.lanMoves.length) {
              const nextLanMove = lineData.lanMoves[nextMoveIndex];

              const nextArrowData = formatStockfishArrow(nextLanMove, lineData.line);
              if (nextArrowData) {

                setStockfishArrows([nextArrowData]); // Set only the next arrow
              }
            } else {
              // Arrows remain clear (cleared at start)
            }
          } else {
            // Arrows remain clear
          }

          // Check if we need to recalculate based on threshold
          if (matchedMoveIndex + 1 >= recalculationThreshold) {
            handleAnalyze(true); // This will clear arrows again
          }
        } else {
          // No matching line, force recalculation
          setIsFollowingCachedLine(false);
          setFollowingLineIndex(-1);
          setMovesMade(0);
          handleAnalyze(true); // This will clear arrows
        }
      } catch (err) {
        console.error('Error processing board move:', err);
      }
    };

    // Set up a MutationObserver to watch for changes to the board
    const observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.type === 'attributes' && mutation.attributeName === 'data-fen') {
          handleBoardMove();
          break;
        }
      }
    });

    // Start observing the board element if it exists
    const boardElement = getBoardElement();
    if (boardElement) {
      observer.observe(boardElement, { attributes: true });
    }

    // Clean up observer on component unmount
    return () => {
      observer.disconnect();
    };
  }, [isAnalyzing, convertedPVs, followingLineIndex, movesMade, recalculationThreshold, lastPlayedMove, getCurrentFEN, uiVersion, handleAnalyze, setLastBoardMoveSAN, lastPlayedMove /* Add dependencies */]);

  // Helper function to update evaluation from string format
  const updateEvaluationFromString = (evalString) => {
    if (typeof evalString === 'string') {
      if (evalString.startsWith('M')) {
        // For mate scores, need to parse the mate number
        const mateText = evalString.substring(1); // Remove the 'M'
        const mateNumber = parseInt(mateText);

        if (!isNaN(mateNumber)) {
          // M positive means White has mate, M negative means Black has mate
          // We don't need to check turn here because mate is always from white's perspective

          // Get the current position FEN to determine side to move
          const currentPositionFEN = getCurrentFEN() || currentPosition;

          // Only track previous evaluation if this is a direct evaluation (not from cache)
          if (!useCachedEvaluation && currentPositionFEN) {
            setPreviousPositionFEN(currentPositionFEN);
            setPreviousEvaluation(evaluation);
          }

          setEvaluation(evalString);
          if (setGlobalEvaluation) {
            setGlobalEvaluation(evalString);
          }

          // For string-based evaluations, we don't have depth information
          // We should only calculate a classification if we're viewing final results
          // (i.e., UI-based interactions, not intermediate engine updates)
          if (!useCachedEvaluation) {
            const sideToMove = currentPositionFEN.split(' ')[1];
            const classification = calculateMoveClassification(previousEvaluation, evalString, sideToMove, previousPositionFEN, currentPositionFEN);

            // Log the classification (for testing)
            // if (classification) {
            //   ;
            // }
          }
        }
      } else {
        // Parse numeric evaluation (+0.35 or -1.2)
        const numericEval = parseFloat(evalString.replace('+', ''));
        if (!isNaN(numericEval)) {
          // Normalize the score - always from white's perspective
          // No need to check turn here because we assume the score is already normalized

          // Get the current position FEN to determine side to move
          const currentPositionFEN = getCurrentFEN() || currentPosition;

          // Only track previous evaluation if this is a direct evaluation (not from cache)
          if (!useCachedEvaluation && currentPositionFEN) {
            setPreviousPositionFEN(currentPositionFEN);
            setPreviousEvaluation(evaluation);
          }

          setEvaluation(numericEval);
          if (setGlobalEvaluation) {
            setGlobalEvaluation(numericEval);
          }

          // For string-based evaluations, we don't have depth information
          // We should only calculate a classification if we're viewing final results
          if (!useCachedEvaluation) {
            const sideToMove = currentPositionFEN.split(' ')[1];
            const classification = calculateMoveClassification(previousEvaluation, numericEval, sideToMove, previousPositionFEN, currentPositionFEN);

            // Log the classification (for testing)
            // if (classification) {
            //   ;
            // }
          }
        }
      }
    }
  };

  // Helper function to calculate move classification based on evaluation change
  const calculateMoveClassification = (previousEval, currentEval, sideToMove, previousFEN, currentFEN) => {
    // Reset classification data at the start
    window.moveClassificationData = {
      classification: null,
      positionFEN: null,
      timestamp: null
    };

    // If we don't have both evaluations, we can't calculate the classification
    if (previousEval === null || currentEval === null || previousFEN === null || currentFEN === null) {


      return null;
    }

    // Check if the positions are consecutive
    if (!areConsecutivePositions(previousFEN, currentFEN)) {

      return null;
    }

    // Handle mate scores
    if (typeof previousEval === 'string' && previousEval.startsWith('M')) {
      // Previous position was a mate
      const classification = 'good';

      // Store the classification data
      window.moveClassificationData = {
        classification: classification,
        positionFEN: currentFEN,
        timestamp: Date.now()
      };

      return classification;
    }

    if (typeof currentEval === 'string' && currentEval.startsWith('M')) {
      // Current position is a mate
      // If it's a positive mate (for the player to move), it's good
      // If it's a negative mate (for the opponent), it's bad
      const mateValue = parseInt(currentEval.substring(1));
      let classification;

      if ((sideToMove === 'w' && mateValue > 0) || (sideToMove === 'b' && mateValue < 0)) {
        classification = 'good';
      } else {
        classification = 'blunder';
      }

      // Store the classification data
      window.moveClassificationData = {
        classification: classification,
        positionFEN: currentFEN,
        timestamp: Date.now()
      };

      return classification;
    }

    // Convert to numeric if needed
    const prevScore = typeof previousEval === 'string' ? parseFloat(previousEval.replace('+', '')) : previousEval;
    const currScore = typeof currentEval === 'string' ? parseFloat(currentEval.replace('+', '')) : currentEval;

    // Calculate evaluation change based on side to move
    // White's perspective: previous - current
    // Black's perspective: current - previous
    const evalChange = sideToMove === 'w'
      ? prevScore - currScore
      : currScore - prevScore;

    // // Log the calculation details for debugging
    // console.log('Move Classification Calculation:', {
    //   previousEval,
    //   currentEval,
    //   sideToMove,
    //   evalChange,
    //   previousFEN,
    //   currentFEN,
    //   areConsecutive: areConsecutivePositions(previousFEN, currentFEN)
    // });

    // Apply thresholds to determine classification
    let classification;
    if (evalChange < -3) {
      classification = 'blunder';
    } else if (evalChange < -1) {
      classification = 'inaccuracy';
    } else {
      classification = 'good';
    }

    // Store the classification data with current position information
    window.moveClassificationData = {
      classification: classification,
      positionFEN: currentFEN,
      timestamp: Date.now()
    };

    return classification;
  };

  // Helper function to determine if two positions are consecutive
  const areConsecutivePositions = (previousFEN, currentFEN) => {
    if (!previousFEN || !currentFEN) return false;

    // Parse the full move numbers from both FENs
    const prevParts = previousFEN.split(' ');
    const currParts = currentFEN.split(' ');

    if (prevParts.length < 6 || currParts.length < 6) return false;

    const prevMoveNumber = parseInt(prevParts[5]);
    const currMoveNumber = parseInt(currParts[5]);
    const prevSideToMove = prevParts[1];
    const currSideToMove = currParts[1];

    // Check if move numbers are consecutive
    // If the previous side to move was black ('b') and current is white ('w'),
    // the full move number should increment by 1
    if (prevSideToMove === 'b' && currSideToMove === 'w') {
      return prevMoveNumber + 1 === currMoveNumber;
    }
    // If same side to move or from white to black, the full move number should stay the same
    else if ((prevSideToMove === currSideToMove) ||
      (prevSideToMove === 'w' && currSideToMove === 'b')) {
      return prevMoveNumber === currMoveNumber;
    }

    return false;
  };

  // Update recalculate button color based on moves made
  useEffect(() => {
    // Determine the current move depth based on UI version
    const currentDepth = uiVersion === '1' && expandedLine === null
      ? moveHistory.length  // In Modern default view
      : movesMade;          // In expanded or Classical view

    if (currentDepth === 0) {
      // Reset to default color when not following a path
      setRecalculateButtonColor(colors.background);
      setRecalculationPercent(0);
      return;
    }

    // Calculate how close we are to the recalculation threshold (as a percentage)
    const percent = Math.min(100, (currentDepth / recalculationThreshold) * 100);
    setRecalculationPercent(percent);

    // Generate a color that transitions from the default color to red
    // Start with the default color for the first move, end with full red by the threshold
    const defaultRGB = hexToRgb(colors.background);
    const targetRGB = hexToRgb('#ff0000'); // Full red

    // Calculate the interpolated color based on progress
    const r = Math.round(defaultRGB.r + (targetRGB.r - defaultRGB.r) * (percent / 100));
    const g = Math.round(defaultRGB.g + (targetRGB.g - defaultRGB.g) * (percent / 100));
    const b = Math.round(defaultRGB.b + (targetRGB.b - defaultRGB.b) * (percent / 100));

    setRecalculateButtonColor(`rgb(${r}, ${g}, ${b})`);

    // Add the pulse animation to the recalculate button when getting close to threshold
    const recalculateBtn = document.querySelector('.recalculate-btn');
    if (recalculateBtn) {
      if (percent > 80 && currentDepth < recalculationThreshold) {
        // Over 80% of the way to threshold, add pulsing animation
        recalculateBtn.style.animation = 'buttonPulse 1.5s infinite';
      } else {
        // Otherwise, remove the animation
        recalculateBtn.style.animation = 'none';
      }
    }
  }, [movesMade, isFollowingCachedLine, recalculationThreshold, colors.background, moveHistory.length, uiVersion, expandedLine]);

  // Utility function to convert hex color to RGB components
  const hexToRgb = (hex) => {
    // Default fallback color if conversion fails
    const defaultRGB = { r: 26, g: 38, b: 53 }; // Corresponding to #1a2635

    try {
      // Remove the # if present
      const cleanHex = hex.replace('#', '');

      // Parse the hex values to RGB
      if (cleanHex.length === 3) {
        // Short form #RGB
        return {
          r: parseInt(cleanHex[0] + cleanHex[0], 16),
          g: parseInt(cleanHex[1] + cleanHex[1], 16),
          b: parseInt(cleanHex[2] + cleanHex[2], 16)
        };
      } else if (cleanHex.length === 6) {
        // Long form #RRGGBB
        return {
          r: parseInt(cleanHex.substring(0, 2), 16),
          g: parseInt(cleanHex.substring(2, 4), 16),
          b: parseInt(cleanHex.substring(4, 6), 16)
        };
      }
    } catch (error) {
      console.error('Error converting hex to RGB:', error);
    }

    return defaultRGB;
  };

  const handlePremiumClick = () => {
    window.open('/premium', '_blank');
    // The current page/modal will remain unchanged
  };

  const handleStockfishClose = () => {
    if (isViewGame) {
      setShowStockfish(false);
    }
  };

  const handleTabSwitch = () => {
    setActiveTab(activeTab === 'main' ? 'settings' : 'main');
    // Reset expanded line when switching tabs
    if (activeTab === 'settings') {
      setExpandedLine(null);
    }
  };

  // Handle UI version change
  const handleVersionChange = (e) => {
    setUiVersion(e.target.value);
    // Reset expanded line when changing versions
    setExpandedLine(null);
    // Return to main tab when changing versions
    setActiveTab('main');
  };

  // Handle expanding a line in Version 1
  const handleExpandLine = (lineNumber) => {
    // If we're expanding, reset the Modern UI state tracking for selected lines
    if (expandedLine !== lineNumber) {
      setSelectedLineIndex(null);
      setCurrentMoveDisplayIndex(0);
      setMoveHistory([]);
    }
    setExpandedLine(expandedLine === lineNumber ? null : lineNumber);
  };

  // Navigate to next/previous line in Version 2 / Expanded V1
  const handleNavigateLine = useCallback((direction) => {
    const maxLines = convertedPVs?.length || 3;
    let newActiveLine = activeLine;

    if (direction === 'next') {
      newActiveLine = activeLine < maxLines ? activeLine + 1 : 1;
    } else {
      newActiveLine = activeLine > 1 ? activeLine - 1 : maxLines;
    }

    setActiveLine(newActiveLine);
    // --- If in Expanded Modern UI, sync expandedLine ---
    if (expandedLine !== null) {
      setExpandedLine(newActiveLine);
    }
    // --- End modification ---
    setCurrentMoveIndex(-1);
    setMovesMade(0);
    setLastBoardMoveSAN(null); // <-- Reset last move display

    // Reset the board to the analysis start position
    const baseAnalysisFEN = originalAnalysisFEN || getCurrentFEN();
    if (window.setPositionDirectly) {

      window.setPositionDirectly(baseAnalysisFEN);
    } else if (window.resetChessBoard) {
      window.resetChessBoard();
      if (baseAnalysisFEN !== 'rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1' && window.setPositionDirectly) {
        window.setPositionDirectly(baseAnalysisFEN);
      }
    } else {
      console.error("Cannot reset board when navigating lines - required functions missing.");
    }
    // --- Update dependencies ---
  }, [activeLine, expandedLine, setExpandedLine, convertedPVs, originalAnalysisFEN, getCurrentFEN, setLastBoardMoveSAN]);

  // Utility function to convert a move in LAN format to an arrow array format with proper line coloring
  const formatStockfishArrow = (move, lineNumber = 1) => {
    if (!move || move.length < 4) return null;

    // Extract from and to squares
    const from = move.substring(0, 2).toLowerCase();
    const to = move.substring(2, 4).toLowerCase();

    // Get the proper color based on line number (New: Red, Black, White for 1, 2, 3)
    let color;
    switch (lineNumber) {
      case 1:
        color = 'gold'; // Red for line 1 (best line)
        break;
      case 2:
        color = 'rgb(76, 175, 80)'; // Black for line 2 (2nd best)
        break;
      case 3:
        color = 'rgb(66, 165, 245)'; // White for line 3 (3rd best)
        break;
      default:
        color = '#4CAF50'; // Default to green
    }

    // Return in the format the Chessboard component expects
    // Omit the opacity value for now to test if it's the source
    return [from, to, color];
    // return [from, to, color, opacity]; // Original line
  };

  // Handler for arrow setting dropdown change
  const handleArrowSettingChange = (e) => {
    const newSetting = e.target.value;
    setArrowSetting(newSetting);
    // Update arrows immediately based on the new setting
    updateBestMoveArrow(newSetting); // Pass the new setting directly
  };

  // Function to update the best move arrow based on available lines
  const updateBestMoveArrow = useCallback((currentArrowSetting = arrowSetting) => { // Use passed setting or state
    // Check the arrow setting first
    if (currentArrowSetting === 'NONE' || !convertedPVs || convertedPVs.length === 0) {
      setStockfishArrows([]);
      return;
    }

    let arrowsToShow = [];

    // --- Handle Hover Arrow (takes precedence) ---
    if (hoveredMove && currentArrowSetting !== 'NONE') {
      const lineNum = hoveredMoveLineNumber || 1;
      const hoverArrowData = formatStockfishArrow(hoveredMove, lineNum);
      if (hoverArrowData) {
        hoverArrowData[2] = '#FFD700'; // Yellow for hover

        arrowsToShow = [hoverArrowData];
        setStockfishArrows(arrowsToShow);
        return; // Show only hover arrow
      }
    }

    // --- Default Logic: Show initial best move(s) ---
    const maxLinesToShow = currentArrowSetting === 'BEST' ? 1 : (currentArrowSetting === 'ALL' ? 3 : 0);
    arrowsToShow = []; // Reset for default logic

    for (let i = 0; i < convertedPVs.length && i < maxLinesToShow; i++) {
      const line = convertedPVs[i];


      if (line && line.bestMove) {

        // Validate the SAN move (line.moveName) for legality check
        const isValidMove = window.isLegalMove ? window.isLegalMove(line.moveName) : true;

        if (isValidMove) {
          const arrowData = formatStockfishArrow(line.bestMove, i + 1);

          if (arrowData) {
            arrowsToShow.push(arrowData);
            if (i === 0) setCurrentArrow(line.bestMove);
            if (currentArrowSetting === 'BEST') {
              break;
            }
          }
        }
      }
    }

    // Set the arrows to display (only the first valid one)
    setStockfishArrows(arrowsToShow);

  }, [
    arrowSetting, // Keep
    convertedPVs, // Keep
    hoveredMove, // Keep
    hoveredMoveLineNumber, // Keep
    setStockfishArrows, // Keep
    setCurrentArrow, // Keep
    // Remove uiVersion, expandedLine, selectedLineIndex, currentMoveDisplayIndex, followingLineIndex, movesMade
  ]);

  // Update the best move arrow whenever relevant state changes
  useEffect(() => {
    updateBestMoveArrow(); // Uses the current arrowSetting from state
  }, [updateBestMoveArrow]); // updateBestMoveArrow dependency includes arrowSetting now

  // Update arrow when hover state changes
  useEffect(() => {
    if (arrowSetting !== 'NONE') { // Only react to hover if arrows are enabled
      if (hoveredMove) {
        // Show the hovered move with corresponding line color
        const arrowData = formatStockfishArrow(hoveredMove, hoveredMoveLineNumber);
        if (arrowData) {
          setStockfishArrows([arrowData]);
        }
      } else {
        // Return to showing the best moves from each line
        updateBestMoveArrow();
      }
    }
  }, [hoveredMove, hoveredMoveLineNumber, arrowSetting, setStockfishArrows, updateBestMoveArrow]); // Depend on arrowSetting

  // Add a modifier to the Version1UI and FullLineDisplay components to track line numbers for hover

  // Modify the onMouseEnter callback in FullLineDisplay > renderMoves, around line 1175:
  // Inside span with onMouseEnter:
  //  onMouseEnter={() => {
  //     // Only show hover effects when arrows are enabled
  //     if (showArrows) {
  //       // Convert SAN move to LAN format for the arrow
  //       try {
  //         // Create a temporary chess instance with the current position up to this point
  //         const tempChess = new Chess(originalAnalysisFEN);
  //         // Apply all moves up to the current index
  //         for (let i = 0; i <= index - 1; i++) {
  //           tempChess.move(moves[i]);
  //         }
  //         // Now try to apply the hovered move
  //         const moveObj = tempChess.move(move);
  //         if (moveObj) {
  //           const lanMove = `${moveObj.from}${moveObj.to}${moveObj.promotion || ''}`;
  //           setHoveredMove(lanMove);
  //           setHoveredMoveLineNumber(lineData.line); // Set the line number
  //         }
  //       } catch (error) {
  //         console.error('Error converting move for hover:', error);
  //       }
  //     }
  //   }}

  // Modify the onMouseEnter callback in Version1UI's div that renders each move, around line 1494:
  // Inside div with onMouseEnter:
  //  onMouseEnter={() => {
  //     // Only show hover effects for valid moves
  //     if (!isDimmedFinal && showArrows) {
  //       // Convert move to LAN format for the arrow
  //       try {
  //         const chess = new Chess(originalAnalysisFEN);
  //         const moveObj = chess.move(moveToDisplay, { sloppy: true });
  //         if (moveObj) {
  //           const lanMove = `${moveObj.from}${moveObj.to}${moveObj.promotion || ''}`;
  //           setHoveredMove(lanMove);
  //           setHoveredMoveLineNumber(index + 1); // Set the line number (index is 0-based, lines are 1-based)
  //         }
  //       } catch (error) {
  //         console.error('Error converting move for hover:', error);
  //       }
  //     }
  //   }}

  // Function to process a user move and check against cached lines
  const processMove = useCallback((move) => {
    if (!isAnalyzing || !move) return;

    // Check if this move matches any cached lines
    const cacheCheck = checkMoveAgainstCache(move);

    if (!cacheCheck) {
      // No cache available, we need to recalculate

      handleAnalyze(true);
      return;
    }



    if (cacheCheck.needsRecalculation) {
      if (cacheCheck.diverged) {
        // Move diverges from the line we were following, recalculate

        setIsFollowingCachedLine(false);
        setFollowingLineIndex(-1);
        setMovesMade(0);
        handleAnalyze(true);
      } else if (cacheCheck.lineIndex >= 0 && cacheCheck.movesMade >= recalculationThreshold) {
        // We've reached the threshold, recalculate

        handleAnalyze(true);
      }
    } else if (cacheCheck.lineIndex >= 0) {
      // Following a cached line, update state
      setIsFollowingCachedLine(true);
      setFollowingLineIndex(cacheCheck.lineIndex);
      setMovesMade(cacheCheck.movesMade);

      // Use the cached evaluation
      setUseCachedEvaluation(true);
      if (cacheCheck.evaluation) {
        const evalValue = cacheCheck.evaluation;

        if (typeof evalValue === 'string' && evalValue.startsWith('M')) {
          setEvaluation(evalValue);
          if (setGlobalEvaluation) {
            setGlobalEvaluation(evalValue);
          }
        } else if (typeof evalValue === 'string') {
          // Handle string format evaluations like "+0.35"
          const numericEval = parseFloat(evalValue.replace('+', ''));
          if (!isNaN(numericEval)) {
            setEvaluation(numericEval);
            if (setGlobalEvaluation) {
              setGlobalEvaluation(numericEval);
            }
          }
        } else if (typeof evalValue === 'number') {
          setEvaluation(evalValue);
          if (setGlobalEvaluation) {
            setGlobalEvaluation(evalValue);
          }
        }
      }
    }
  }, [isAnalyzing, recalculationThreshold, setGlobalEvaluation]); // Removed handleAnalyze to break circular dependency

  // Handler for board position changes
  const handleBoardPositionChanged = useCallback((fen, moveIndex) => {

    setCurrentBoardFEN(fen);
    setCurrentBoardMoveIndex(parseInt(moveIndex));

    // Also update the move index in the UI to stay in sync
    setCurrentMoveIndex(parseInt(moveIndex));
  }, []);

  // Register the board position change handler
  useEffect(() => {
    // Add handler for board position changes
    window.onBoardPositionChanged = handleBoardPositionChanged;

    return () => {
      // Clean up
      delete window.onBoardPositionChanged;
    };
  }, [handleBoardPositionChanged]);

  // Function to get the current board position
  const getCurrentBoardPosition = useCallback(() => {
    if (window.getCurrentBoardPosition) {
      return window.getCurrentBoardPosition();
    }
    return null;
  }, []);

  // Process moves up to a specific index in a line
  const processMovesToIndex = (targetIndex, lineData) => {


    // Add logs for window operations that affect board state
    if (window.setPositionDirectly && originalAnalysisFEN) {

    }

    if (window.resetChessBoard) {

    }

    if (window.makeSequentialMoves) {

    }

    if (window.makeChessMove) {

    }

    if (targetIndex < -1 || targetIndex >= lineData?.moves.length) {
      console.error(`Invalid move index: ${targetIndex}`);
      return;
    }

    // Set the current move index in the UI
    setCurrentMoveIndex(targetIndex);

    // In expanded view, track the number of moves made
    const previousMoveIndex = currentMoveIndex;

    // Calculate new move depth for recalculation tracking
    // If moving forward, increment depth
    // If moving backward, decrement depth
    let newMoveDepth = movesMade;

    if (targetIndex > previousMoveIndex) {
      // Moving forward, increment by the number of new moves
      newMoveDepth += (targetIndex - previousMoveIndex);
    } else if (targetIndex < previousMoveIndex) {
      // Moving backward, decrement by the number of moves undone
      newMoveDepth = Math.max(0, newMoveDepth - (previousMoveIndex - targetIndex));
    }

    // Update the moves made state
    setMovesMade(newMoveDepth);

    // Check if we've reached the recalculation threshold
    if (newMoveDepth >= recalculationThreshold && targetIndex !== -1) {
      // Small delay to allow the moves to be rendered before recalculating
      setTimeout(() => {
        // Get the current board position after all moves have been applied
        const newFEN = window.getCurrentBoardPosition ? window.getCurrentBoardPosition() : getCurrentFEN();

        // Reset state before analyzing
        setCurrentMoveIndex(-1);
        setMovesMade(0);

        // Use the current position for analysis
        if (newFEN) {

          // Store this FEN to preserve for analysis
          setOriginalAnalysisFEN(newFEN);
          handleAnalyze(true);
        } else {
          // Fallback to standard analysis
          handleAnalyze(true);
        }
      }, 50); // Reduced from 300ms to 50ms
    }

    // If we're clicking the first move in the line (index 0), we can directly use makeChessMove
    // This is the first step of our implementation for move clicking
    if (previousMoveIndex === -1 && targetIndex === 0 && window.makeChessMove) {

      const result = window.makeChessMove(lineData.moves[0]);
      if (result) {

        return;
      } else {
        console.error(`Failed to make move ${lineData.moves[0]} on the board`);
      }
    }

    // If window.makeSequentialMoves is available, calculate the sequence of moves to apply
    // This will be updated in future implementation phases to handle clicking later moves
    if (window.makeSequentialMoves && window.getCurrentMoveIndex && window.getCurrentBoardPosition) {
      try {
        // Get the current board position and move index
        const currentBoardPosition = window.getCurrentBoardPosition();
        const currentIdx = parseInt(window.getCurrentMoveIndex());

        // Use originalAnalysisFEN instead of getCurrentFEN() for the analysis base position
        const baseAnalysisFEN = originalAnalysisFEN || getCurrentFEN();




        // If resetting to initial position (When "Start" button is clicked)
        if (targetIndex === -1) {


          if (window.setPositionDirectly) {
            // Set the position directly to the original analysis starting position
            const result = window.setPositionDirectly(baseAnalysisFEN);


            // Notify any other components that we've reset to the analysis starting position
            if (window.onBoardPositionChanged) {
              window.onBoardPositionChanged(baseAnalysisFEN, -1);
            }

            // Return immediately as we've completed the reset
            return;
          } else if (window.resetChessBoard) {
            // Fallback if setPositionDirectly is not available
            window.resetChessBoard();

            // If we need to set a custom analysis position after the reset
            if (baseAnalysisFEN !== 'rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1') {
              if (window.setPositionDirectly) {
                window.setPositionDirectly(baseAnalysisFEN);
              }
            }

            // Notify any other components that we've reset to the analysis starting position
            if (window.onBoardPositionChanged) {
              window.onBoardPositionChanged(baseAnalysisFEN, -1);
            }
          }
          return;
        }

        // Already at target index
        if (currentIdx === targetIndex) {

          return;
        }

        // If we're at a known index in this line
        if (currentIdx >= -1 && currentIdx < lineData.moves.length) {
          // Only apply the moves between the current index and target index
          if (targetIndex > currentIdx) {
            // Moving forward, apply only the incremental moves
            const incrementalMoves = lineData.moves.slice(currentIdx + 1, targetIndex + 1);

            if (incrementalMoves.length > 0) {
              window.makeSequentialMoves(incrementalMoves);
            }
            return;
          } else if (targetIndex < currentIdx) {
            // Moving backward, we need to reset and replay

            try {
              // Create a new instance with the analysis position
              const backwardChess = new Chess(baseAnalysisFEN);

              // Apply moves up to the target index
              for (let i = 0; i <= targetIndex; i++) {
                backwardChess.move(lineData.moves[i]);
              }

              // Get the FEN for the target position
              const targetFEN = backwardChess.fen();

              // Set the position directly to the target FEN
              if (window.setPositionDirectly) {

                window.setPositionDirectly(targetFEN);
              } else {
                // Fallback to resetting and then applying moves
                if (window.resetChessBoard) {

                  window.resetChessBoard();

                  // Set custom start position if needed
                  if (baseAnalysisFEN !== 'rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1' && window.setPositionDirectly) {
                    window.setPositionDirectly(baseAnalysisFEN);
                  }

                  // Apply moves to target
                  if (targetIndex >= 0) {
                    const movesToApply = lineData.moves.slice(0, targetIndex + 1);

                    window.makeSequentialMoves(movesToApply);
                  }
                }
              }
            } catch (error) {
              console.error('Error moving backward:', error);
            }
            return;
          }
        }

        // If we're at an unknown position or not on this line, we need to verify
        // Check if the current board position matches one of the positions in the line
        let onSameLine = false;
        let currentPositionIndex = -1;

        // Function to check if a FEN matches the expected position
        const matchesExpectedPosition = (actualFEN, expectedFEN) => {
          if (!actualFEN || !expectedFEN) return false;

          // Compare only the piece positions and turn from the FEN
          const actualParts = actualFEN.split(' ');
          const expectedParts = expectedFEN.split(' ');

          // Main position and turn must match
          return actualParts[0] === expectedParts[0] && actualParts[1] === expectedParts[1];
        };

        // Create a temporary chess instance for position checking
        const tempChess = new Chess(baseAnalysisFEN);

        // Check if current position matches the starting position
        if (matchesExpectedPosition(currentBoardPosition, baseAnalysisFEN)) {

          onSameLine = true;
          currentPositionIndex = -1;
        } else {
          // Check if current position matches any position in the line
          for (let i = 0; i < lineData.moves.length; i++) {
            try {
              // Apply this move
              tempChess.move(lineData.moves[i]);

              // Check if current board position matches
              if (matchesExpectedPosition(currentBoardPosition, tempChess.fen())) {

                onSameLine = true;
                currentPositionIndex = i;
                break;
              }
            } catch (error) {
              console.error(`Error checking position at move ${i}:`, error);
              // Continue checking next moves
            }
          }
        }



        // If we're on the same line, apply only the needed moves
        if (onSameLine) {
          if (targetIndex > currentPositionIndex) {
            // Apply only the incremental moves forward
            const movesToApply = lineData.moves.slice(currentPositionIndex + 1, targetIndex + 1);

            if (movesToApply.length > 0) {
              window.makeSequentialMoves(movesToApply);
            }
          } else if (targetIndex < currentPositionIndex) {
            // Moving backward - need to set the position directly
            try {
              // New instance with the analysis position
              const backwardChess = new Chess(baseAnalysisFEN);

              // Apply moves up to the target index
              for (let i = 0; i <= targetIndex; i++) {
                backwardChess.move(lineData.moves[i]);
              }

              // Set the position directly
              const targetFEN = backwardChess.fen();

              window.setPositionDirectly(targetFEN);
            } catch (error) {
              console.error('Error moving backward:', error);
            }
          } else {

          }
        } else {
          // Not on this line - we need to reset and apply all moves


          try {
            // Start from the analysis base position
            if (window.setPositionDirectly) {
              window.setPositionDirectly(baseAnalysisFEN);
            } else if (window.resetChessBoard) {
              window.resetChessBoard();

              // Set custom position if needed
              if (baseAnalysisFEN !== 'rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1' && window.setPositionDirectly) {
                window.setPositionDirectly(baseAnalysisFEN);
              }
            }

            // Apply moves to target if needed
            if (targetIndex >= 0) {
              const movesToApply = lineData.moves.slice(0, targetIndex + 1);


              // Apply one move at a time to ensure proper sequencing
              for (let i = 0; i <= targetIndex; i++) {
                const moveToApply = lineData.moves[i];
                try {

                  const result = window.makeChessMove(moveToApply);
                  if (!result) {
                    console.error(`Failed to apply move: ${moveToApply}`);
                    break;
                  }
                } catch (moveError) {
                  console.error(`Error applying move ${moveToApply}:`, moveError);
                  break;
                }
              }
            }
          } catch (error) {
            console.error('Error applying move sequence:', error);
          }
        }
      } catch (error) {
        console.error('Error in processMovesToIndex:', error);
      }
    }
  };

  // Component to render a full line
  const FullLineDisplay = ({ lineData, lastBoardMoveSAN, setLastBoardMoveSAN, analysisStartTurn }) => {
    // Get appropriate line name based on line number
    const getLineName = (lineNumber) => {
      switch (lineNumber) {
        case 1: return "Top Engine Line";
        case 2: return "2nd Best Line";
        case 3: return "3rd Best Line";
        default: return `Line ${lineNumber}`;
      }
    };

    // Function to render the moves as clickable buttons
    const renderMoves = () => {
      const moves = lineData.moves;
      const startsWithBlack = analysisStartTurn === 'b';

      // Function to handle clicking on a move - NOW ASYNC
      const handleMoveClick = async (moveIndex, move) => {

        // ---> Clear arrows INSTANTLY <---
        setStockfishArrows([]);


        // Update UI State
        setCurrentMoveIndex(moveIndex);
        setMovesMade(moveIndex + 1);
        const newMoveHistoryEntry = { lineIndex: lineData.line - 1, moveIndex, displayIndex: moveIndex };
        setMoveHistory([newMoveHistoryEntry]);
        setSelectedLineIndex(lineData.line - 1);
        setCurrentMoveDisplayIndex(moveIndex);

        // --- Board Update Logic ---
        const baseAnalysisFEN = originalAnalysisFEN || getCurrentFEN();
        // 1. Reset board
        if (window.setPositionDirectly) {
          window.setPositionDirectly(baseAnalysisFEN);
        } else if (window.resetChessBoard) {
          window.resetChessBoard();
          if (baseAnalysisFEN !== 'rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1' && window.setPositionDirectly) {
            window.setPositionDirectly(baseAnalysisFEN);
          }
        } else {
          console.error("Cannot reset board (FullLineDisplay) - required functions missing.");
          return;
        }

        // Wait for reset
        await new Promise(resolve => setTimeout(resolve, 150));

        // Get moves to play
        const movesToPlay = lineData.moves.slice(0, moveIndex + 1);

        // Play sequence with delays
        let sequencePlayedSuccessfully = false;
        if (window.makeChessMove) {
          let successInLoop = true;
          for (let i = 0; i < movesToPlay.length; i++) {
            const moveSAN = movesToPlay[i];
            // --- Use try-catch for makeChessMove ---
            try {
              const result = window.makeChessMove(moveSAN);
              if (!result) {
                console.error(`Failed to make move (FullLineDisplay) ${moveSAN}`);
                successInLoop = false;
                break;
              }
            } catch (e) {
              console.error(`Error during makeChessMove for ${moveSAN}:`, e);
              successInLoop = false;
              break;
            }
            // --- End try-catch ---
            await new Promise(resolve => setTimeout(resolve, 350));
          }
          if (successInLoop) {
            sequencePlayedSuccessfully = true;
          }
        } else {
          console.error("Cannot make moves (FullLineDisplay) - makeChessMove missing.");
        }

        // ---> Set NEXT Arrow (if sequence played successfully) <---
        if (sequencePlayedSuccessfully) {
          // Check arrow setting before proceeding
          if (arrowSetting !== 'NONE') {
            const nextMoveIndex = moveIndex + 1;
            const nextLanMove = lineData.lanMoves?.[nextMoveIndex];

            if (nextLanMove) {

              const nextArrowData = formatStockfishArrow(nextLanMove, lineData.line);
              if (nextArrowData) {

                setStockfishArrows([nextArrowData]);
              }
            } else {

              // Arrows remain clear (already cleared at start)
            }
          } else {

            // Arrows remain clear
          }
        } else {

          // Arrows remain clear
        }
      };

      // --- Build the elements with move numbers --- START
      const elements = [];
      let moveNumber = 1; // Start with move number 1

      for (let index = 0; index < moves.length; index++) {
        const move = moves[index];
        const isWhiteMove = startsWithBlack ? (index % 2 !== 0) : (index % 2 === 0);
        const isFirstMove = index === 0;

        // Add move number/dots before White's move or Black's first move if starting turn is black
        if (isWhiteMove) {
          // For White's move, always add the number.
          elements.push(
            <span key={`num-${index}`} style={{ marginLeft: '5px', paddingRight: '0px', paddingLeft: '0px', fontWeight: 'bold', color: 'black' }}>
              {`${moveNumber}.`}
            </span>
          );
        } else if (startsWithBlack && isFirstMove) {
          // For Black's first move when Black starts, add number...
          elements.push(
            <span key={`num-${index}`} style={{ marginLeft: '5px', paddingRight: '0px', paddingLeft: '0px', fontWeight: 'bold', color: 'black' }}>
              {`${moveNumber}...`}
            </span>
          );
        }

        // Add the clickable move itself
        elements.push(
          <span
            key={index} // Use original index for key
            onClick={() => handleMoveClick(index, move)} // Pass original index and move SAN
            onMouseEnter={() => {
              // Only show hover effects when arrows are enabled
              if (arrowSetting !== 'NONE') { // Check arrow setting
                try { // Added try-catch for safety
                  // --- UPDATED CHESS INSTANCE ---
                  // Create a temporary chess instance using the FEN from which the current line was analyzed
                  const tempChess = new Chess(originalAnalysisFEN);

                  // Apply all previous moves up to this point IN THIS LINE
                  for (let i = 0; i < index; i++) {
                    // Need to handle potential errors if a move in the sequence becomes invalid
                    // This might happen if the analysis returns an unexpected sequence
                    const result = tempChess.move(moves[i]);
                    if (!result) {
                      console.error(`Hover error: Could not apply previous move ${moves[i]} at index ${i} from FEN ${originalAnalysisFEN}`);
                      return; // Stop processing if a previous move fails
                    }
                  }

                  // Apply this move to get destination square
                  const moveResult = tempChess.move(move);

                  if (moveResult) {
                    // Set hovered move with info from this specific move
                    // Use the LAN format for the arrow, SAN for display is fine here but arrow needs LAN
                    const lanMove = `${moveResult.from}${moveResult.to}${moveResult.promotion || ''}`;
                    setHoveredMove(lanMove); // Use LAN for arrow consistency
                    setHoveredMoveLineNumber(expandedLine || activeLine || 1); // Use active line for V2/Expanded V1
                  } else {
                    // Log if the target move itself is invalid from the derived position
                    console.error(`Hover error: Could not apply target move ${move} at index ${index} from FEN ${tempChess.fen()}`);
                  }
                } catch (error) {
                  console.error(`Error during hover move calculation for ${move}:`, error);
                }
              }
            }}
            onMouseLeave={() => {
              if (arrowSetting !== 'NONE') { // Check arrow setting
                setHoveredMove(null);
              }
            }}
            style={{
              cursor: 'pointer',
              padding: '0 2px',
              borderRadius: '2px',
              backgroundColor: currentMoveIndex === index ? lineData.line === 1 ? 'rgba(255, 215, 0, 1)' : lineData.line === 2 ? 'rgba(76, 175, 80, 1)' : 'rgba(66, 165, 245, 1)' : 'transparent',
              color: currentMoveIndex === index ? 'black' : 'inherit',
              fontWeight: currentMoveIndex === index ? 'bold' : 'inherit',
              transition: 'all 0.2s ease',
              display: 'inline-block',
            }}
          >
            {move} {/* Display the original move SAN */}
          </span>
        );

        // Increment move number after Black's move
        if (!isWhiteMove) {
          moveNumber++;
        }
      }
      // --- Build the elements with move numbers --- END

      return (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-start',
          height: '40px',
          gap: '3px',
        }}>
          {/* --- Render the generated elements --- */}
          {elements}
        </div>
      );
    };

    return (
      <div style={{


        width: '100%',
        gap: '5px',
        height: '100%',
        maxWidth: '100%',

      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: lineData.line === 1 ? '#ffd70050' : lineData.line === 2 ? '#4CAF5050' : '#42A5F550',
          height: '80px', // no longer rely on padding...
          width: '100%',
          border: `4px solid ${lineData.line === 1 ? 'gold' : lineData.line === 2 ? '#4CAF50' : '#42A5F5'}`,
        }}>
          {/* Left section: Back arrow and line identifier */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            minWidth: 'fit-content',
            margin: '0px 12px',
          }}>
            {/* Back arrow (conditionally rendered) */}

            <div style={{

              background: lineData.line === 1 ? 'gold' : lineData.line === 2 ? '#4CAF50' : '#42A5F5',
              color: 'black',
              borderRadius: '2px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '14px',
              fontWeight: 'bold',
              marginRight: '8px',
              padding: '8px 12px',
              position: 'relative',
              bottom: '10px',
            }}>
              {getLineName(lineData.line)}
              {/* Right section: Evaluation */}
              <div style={{
                position: 'absolute',
                top: '32px',
                color: lineData.evaluation.startsWith('-') ? '#f44336' : '#4CAF50',
                fontWeight: 'bold',
                fontSize: '14px',
                whiteSpace: 'nowrap',
                paddingLeft: '3px',
                backgroundColor: 'black',
                paddingRight: '5px',
                borderRadius: '5px',
                paddingTop: '3px',
                paddingBottom: '3px',
                fontStyle: useCachedEvaluation ? 'italic' : 'normal',
                zIndex: 500
              }}>
                {lineData.evaluation}
              </div>
            </div>

          </div>

          {/* Middle section: Moves - Change from scroll to hidden overflow */}
          <div style={{
            flex: 1,
            overflow: 'auto', // Changed from 'auto' to 'hidden' --> Full line scrolling
            scrollbarColor: lineData.line === 1 ? '#FFD700 transparent' : lineData.line === 2 ? '#4CAF50 transparent' : '#42A5F5 transparent',
            scrollbarWidth: 'thin',
            paddingBottom: '15px',
            whiteSpace: 'nowrap',
            fontFamily: 'monospace',
            fontSize: '16px',
            textOverflow: 'ellipsis',



          }}>
            {renderMoves()}
          </div>


        </div>
      </div>
    );
  };

  // Version 1 UI - Top moves with expandable lines
  const Version1UI = () => {
    const getMoveLabel = (index) => {
      switch (index) {
        case 0: return "Best Move:";
        case 1: return "2nd Best:";
        case 2: return "3rd Best:";
        default: return `Move ${index + 1}:`;
      }
    };

    // Enhanced function to handle clicking on a move in the UI
    const handleMoveClick = (move, lineIndex, moveIndex) => {
      const isContinuingSequence = selectedLineIndex === lineIndex;
      if (!isContinuingSequence) { /* ... reset state ... */ }

      if (window.makeChessMove) {
        const result = window.makeChessMove(move);

        if (result) {
          // ---> Clear previous arrows immediately <---
          setStockfishArrows([]);

          // Update state for UI tracking
          setSelectedLineIndex(lineIndex);
          const nextDisplayIndex = currentMoveDisplayIndex + 1;
          setCurrentMoveDisplayIndex(nextDisplayIndex);
          setMovesMade(prev => prev + 1);
          // ... update history, check threshold ...

          // ---> Attempt to show NEXT arrow <---
          // Check arrow setting first
          if (arrowSetting !== 'NONE') {
            const lineData = convertedPVs[lineIndex];
            if (lineData && lineData.lanMoves && nextDisplayIndex < lineData.lanMoves.length) {
              const nextLanMove = lineData.lanMoves[nextDisplayIndex];
              const nextArrowData = formatStockfishArrow(nextLanMove, lineData.line);
              if (nextArrowData) {
                setStockfishArrows([nextArrowData]); // Set only the next arrow
              }
            } else {
              // Keep arrows clear (cleared at start of block)
            }
          } else {
            // Keep arrows clear
          }

          // Check if we need to trigger auto-analysis
          if (movesMade + 1 >= recalculationThreshold) {

            handleAnalyze(true); // This will clear arrows again
          }
        }
      }
    };

    // Check if we're using real data (from Stockfish) or sample data
    const isUsingRealData = convertedPVs && convertedPVs.length > 0;

    // Use real PV data if available
    const pvData = isUsingRealData ? convertedPVs : SAMPLE_PVS;

    // Verify move legality by checking if we can make the first move of each line
    // This helps ensure we don't display illegal moves after recalculation
    const verifyMoveIsLegal = (move) => {
      if (!window.isLegalMove) return true; // If no validation function, assume legal

      try {
        return window.isLegalMove(move);
      } catch (error) {
        console.error('Error validating move legality:', error);
        return true; // Assume legal if error occurs during validation
      }
    };

    // Show loading indicator if currently evaluating AND UI should refresh
    if (isEvaluating && shouldRefreshUI) {
      return (
        <div style={{
          display: 'flex',
          width: '100%',
          justifyContent: 'center',
          alignItems: 'center',
          padding: '10px'
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            background: 'rgba(0, 0, 0, 0.2)',
            padding: '8px 16px',
            borderRadius: '4px'
          }}>
            <i className="fa-solid fa-chess-knight fa-bounce" style={{
              fontSize: '18px',
              color: isFollowingCachedLine && movesMade > 0 ? recalculateButtonColor : '#4CAF50'
            }}></i>
            <span>Calculating...</span>
            <div style={{
              display: 'flex',
              gap: '4px'
            }}>
              <div className="loading-dot" style={{
                width: '6px',
                height: '6px',
                backgroundColor: isFollowingCachedLine && movesMade > 0 ? recalculateButtonColor : '#4CAF50',
                borderRadius: '50%',
                animation: 'pulse 1s infinite',
                animationDelay: '0s'
              }}></div>
              <div className="loading-dot" style={{
                width: '6px',
                height: '6px',
                backgroundColor: isFollowingCachedLine && movesMade > 0 ? recalculateButtonColor : '#4CAF50',
                borderRadius: '50%',
                animation: 'pulse 1s infinite',
                animationDelay: '0.2s'
              }}></div>
              <div className="loading-dot" style={{
                width: '6px',
                height: '6px',
                backgroundColor: isFollowingCachedLine && movesMade > 0 ? recalculateButtonColor : '#4CAF50',
                borderRadius: '50%',
                animation: 'pulse 1s infinite',
                animationDelay: '0.4s'
              }}></div>
            </div>
          </div>
        </div>
      );
    }

    // Show a message if we don't have analysis data yet (not loading and no real data)
    if (!isUsingRealData && isAnalyzing) {
      // Check if we're currently analyzing or if we just don't have data yet
      // if (isEvaluating && shouldRefreshUI) { ... Calculating ... }

      // --- Modified "Waking up" Condition ---
      if (isAnalyzing && !engineReady) {
        // If analysis is active BUT engine is not ready yet
        return (
          <div style={{
            display: 'flex',
            flexDirection: 'row',
            width: '100%',
            justifyContent: 'center',
            alignItems: 'center',
            textAlign: 'center',
            opacity: 0.8
          }}>
            <i className="fa-solid fa-chess-knight fa-bounce" style={{ fontSize: '24px', marginBottom: '', color: '#4CAF50' }}></i>
            &nbsp;&nbsp;<p style={{ fontSize: '18px', fontStyle: 'italic' }}>Waking up Stockfish...</p>
          </div>
        );
      }
    }

    // Check if we should show the Calculating... state (This comes AFTER the Waking up check)
    if (isEvaluating && shouldRefreshUI) {
      return (
        // ... Calculating... JSX ...
        <div style={{
          display: 'flex',
          width: '100%',
          justifyContent: 'center',
          alignItems: 'center',
          padding: '10px'
        }}>
          {/* ... Content of calculating div ... */}
        </div>
      );
    }

    // If none of the above, proceed to render the PV data (or initial prompt if analysis hasn't run)
    // Re-check isUsingRealData here for the case where engine is ready but no PVs yet
    if (!isUsingRealData && isAnalyzing && !engineReady) {
      // This case might occur briefly after engine ready but before first PV
      // Or if analysis completes with no valid PVs
      return (
        <div style={{
          display: 'flex', flexDirection: 'row', width: '100%',
          justifyContent: 'center', alignItems: 'center', textAlign: 'center', opacity: 0.8
        }}>
          <i className="fa-solid fa-chess-knight" style={{ fontSize: '24px', color: '#4CAF50' }}></i>
          &nbsp;&nbsp;<p style={{ fontSize: '18px', fontStyle: 'italic' }}>Waking up Stockfish...</p>
        </div>
      );
    }

    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        width: expandedLine ? 'calc(100% - 8px)' : '100%',
        gap: '10px',
        marginLeft: expandedLine ? '0px' : '0px',
        marginTop: expandedLine ? '' : '0px',
      }}>
        {expandedLine ? (
          // Show full line when expanded - use the same component as Version2UI for full lines
          <FullLineDisplay
            lineData={pvData[expandedLine - 1]}
            lastBoardMoveSAN={lastBoardMoveSAN}
            setLastBoardMoveSAN={setLastBoardMoveSAN}
            analysisStartTurn={analysisStartTurn} // +++ Pass prop +++
          />
        ) : (
          // Show only top moves from each line in a row
          <div style={{
            display: 'flex',
            justifyContent: 'flex-start', // Changed from space-between
            width: '100%',
            gap: '8px', // Keep overall gap between boxes
            flexWrap: 'wrap',
          }}>
            {pvData.map((pv, index) => {
              // Determine if this line is the one being followed or if it's dimmed
              const isFollowed = isFollowingCachedLine && followingLineIndex === index;

              // Modified dimming logic:
              // Only dim if evaluating AND this is not the selected line
              const isDimmed = isEvaluating && selectedLineIndex !== index;

              // Determine which move to display based on the current move display index
              // If this is the selected line, we need to show the next move in the sequence
              let moveToDisplay = pv.moveName; // Default to first move

              if (selectedLineIndex === index) {
                // For the selected line, show the next move in the original PV sequence
                // This is our "pseudo-cached" next move
                if (pv.moves && pv.moves.length > currentMoveDisplayIndex) {
                  moveToDisplay = pv.moves[currentMoveDisplayIndex];
                } else if (pv.moves && pv.moves.length > 0) {
                  // If we've reached the end of available moves, show the last move
                  // This prevents undefined from showing if we click beyond available moves
                  moveToDisplay = pv.moves[pv.moves.length - 1];
                }
              }

              // Check if this move is legal in the current position
              const isMoveValid = verifyMoveIsLegal(moveToDisplay);

              // Only dim if not valid - selected line should never be dimmed during processing
              const isDimmedFinal = isDimmed || (!isMoveValid && selectedLineIndex !== index);


              return (
                // --- Outer Box Container ---
                <div key={pv.line}
                  onClick={() => {
                    // Always allow clicking on the selected line button
                    // For other buttons, only if they're not dimmed and the move is valid
                    if (selectedLineIndex === index || !isDimmedFinal) {
                      handleMoveClick(moveToDisplay, index, currentMoveDisplayIndex);
                    }
                  }}
                  onMouseEnter={() => {
                    // Only show hover effects for valid moves
                    if (!isDimmedFinal && arrowSetting !== 'NONE') { // Check arrow setting
                      // Convert move to LAN format for the arrow
                      try {
                        const chess = new Chess(originalAnalysisFEN);
                        const moveObj = chess.move(moveToDisplay, { sloppy: true });
                        if (moveObj) {
                          const lanMove = `${moveObj.from}${moveObj.to}${moveObj.promotion || ''}`;
                          setHoveredMove(lanMove);
                          setHoveredMoveLineNumber(index + 1); // Set the line number (index is 0-based, lines are 1-based)
                        }
                      } catch (error) {
                        console.error('Error converting move for hover:', error);
                      }
                    }
                  }}
                  onMouseLeave={() => {
                    if (arrowSetting !== 'NONE') { // Check arrow setting
                      setHoveredMove(null);
                    }
                  }}
                  style={{
                    flex: '1', // Keep flexible width
                    minWidth: '120px', // Adjusted minWidth slightly
                    background: pv.line === 1 ? '#ffd70050' : pv.line === 2 ? '#4caf5050' : '#42A5F550', // Existing background
                    border: `2px solid ${pv.line === 1 ? 'gold' : pv.line === 2 ? '#4CAF50' : '#42A5F5'}`, // Existing border
                    borderRadius: '6px', // Slightly larger radius
                    cursor: selectedLineIndex === index ? 'pointer' : (isDimmedFinal ? 'default' : 'pointer'),
                    transition: 'all 0.2s ease',
                    boxShadow: isFollowed ? '0 0 8px rgba(76, 175, 80, 0.8)' : '0 2px 4px rgba(0,0,0,0.1)',
                    opacity: selectedLineIndex === index ? 1 : (isDimmedFinal ? 0.5 : 1),
                    position: 'relative', // Keep relative for absolute positioned children
                    padding: '4px 6px', // Unified padding
                    display: 'flex', // Use flex for internal layout
                    flexDirection: 'column', // Stack internal rows vertically
                    justifyContent: 'space-around', // Push top/bottom rows apart
                    height: '110px',
                    minHeight: '60px', // Ensure a minimum height for consistency
                    ':hover': {
                      background: selectedLineIndex === index ? colors.tertiary : (isDimmedFinal ? 'rgba(26, 38, 53, 1)' : colors.tertiary),
                    } // Keep existing hover
                  }}>

                  {/* --- Top Row: Line Label & Expand Icon --- */}
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between', // Space out label and icon
                    alignItems: 'start',
                    width: '100%',
                    marginBottom: '3px', // Add space below the top row
                  }}>
                    {/* Line Label Group */}
                    <div style={{
                      fontSize: '12px',
                      fontWeight: 'bold',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px', // Space between circle and text
                    }}>
                      <span style={{ /* Existing line circle styles */
                        padding: '2px 5px',
                        background: pv.line === 1 ? 'gold' : pv.line === 2 ? '#4CAF50' : '#42A5F5', color: 'black', borderRadius: '2px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '14px', fontWeight: 'bold'
                      }}>
                        {/* {pv.line} */}{getMoveLabel(index)}
                      </span>

                    </div>

                    {/* Expand Icon (Only if not dimmed) */}
                    {!isDimmedFinal && (
                      <div
                        onClick={(e) => {
                          e.stopPropagation(); // Prevent the move from being played
                          handleExpandLine(pv.line);
                          setActiveLine(pv.line); // Set active line to match the expanded line
                        }}
                        title="EXPAND"
                        style={{
                          width: '30px', // Slightly larger touch target
                          height: '30px',
                          display: 'flex',
                          alignItems: 'start',
                          justifyContent: 'center',
                          color: `${pv.line === 1 ? 'gold' : pv.line === 2 ? '#4CAF50' : '#42A5F5'}`,
                          fontSize: '24px',
                          cursor: 'pointer',
                          zIndex: 2,
                          borderRadius: '4px', // Add subtle background on hover
                          ':hover': {
                            backgroundColor: 'rgba(255, 255, 255, 0.1)',
                            color: 'white',
                          }
                        }}
                      >
                        <i className="fa-solid fa-maximize"></i>
                      </div>
                    )}
                  </div>

                  {/* --- Bottom Row: Move & Evaluation --- */}
                  <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between', // Space out move and score
                    alignItems: 'baseline', // Align text baselines
                    width: '100%',
                    marginTop: 'auto', // Push to bottom if extra space
                  }}>
                    {/* Move */}
                    <span
                      style={{
                        fontWeight: 'bold',
                        fontSize: '30px', // Slightly larger move font
                        lineHeight: '1.1', // Adjust line height
                        color: 'white', // Explicit white color
                        cursor: selectedLineIndex === index ? 'pointer' : (isDimmedFinal ? 'default' : 'pointer') // Inherit cursor
                      }}
                    >
                      {moveToDisplay}
                    </span>

                    {/* Evaluation */}
                    <span style={{
                      color: pv.evaluation.startsWith('-') ? '#f44336' : '#4CAF50', // Existing color logic
                      fontWeight: 'bold',
                      fontSize: '18px', // Keep score font size
                      fontStyle: (useCachedEvaluation && followingLineIndex === index) ? 'italic' : 'normal', // Existing style
                      // marginLeft: '8px', // Add space between move and score
                      padding: '2px 5px',

                    }}>
                      {pv.evaluation}
                    </span>
                  </div>

                  {/* --- Optional Checkmark (Keep existing absolute positioning) --- */}
                  {isFollowed && (
                    <div style={{
                      position: 'absolute',
                      top: '-8px',
                      right: '-8px',
                      width: '20px',
                      height: '20px',
                      borderRadius: '50%',
                      backgroundColor: '#4CAF50',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'white',
                      fontSize: '14px',
                      fontWeight: 'bold',
                      border: '2px solid white',
                    }}>
                      <i className="fa-solid fa-check" style={{ fontSize: '10px' }}></i>
                    </div>
                  )}

                </div> // End Outer Box Container
              );
            })}
          </div> // End Main Flex Container
        )}
      </div>
    );
  };

  // Version 2 UI - Top move with full sequence and navigation
  const Version2UI = () => {
    // Check if we're using real data (from Stockfish) or sample data
    const isUsingRealData = convertedPVs && convertedPVs.length > 0;

    // Use real PV data if available
    const pvData = isUsingRealData
      ? convertedPVs[activeLine - 1]
      : null;

    // Show loading indicator if currently evaluating AND UI should refresh
    if (isEvaluating && shouldRefreshUI) {
      return (
        <div style={{
          display: 'flex',
          width: '100%',
          justifyContent: 'center',
          alignItems: 'center',
          padding: '10px'
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            background: 'rgba(0, 0, 0, 0.2)',
            padding: '8px 16px',
            borderRadius: '4px'
          }}>
            <i className="fa-solid fa-chess-knight fa-bounce" style={{
              fontSize: '18px',
              color: isFollowingCachedLine && movesMade > 0 ? recalculateButtonColor : '#4CAF50'
            }}></i>
            <span>Calculating...</span>
            <div style={{
              display: 'flex',
              gap: '4px'
            }}>
              <div className="loading-dot" style={{
                width: '6px',
                height: '6px',
                backgroundColor: isFollowingCachedLine && movesMade > 0 ? recalculateButtonColor : '#4CAF50',
                borderRadius: '50%',
                animation: 'pulse 1s infinite',
                animationDelay: '0s'
              }}></div>
              <div className="loading-dot" style={{
                width: '6px',
                height: '6px',
                backgroundColor: isFollowingCachedLine && movesMade > 0 ? recalculateButtonColor : '#4CAF50',
                borderRadius: '50%',
                animation: 'pulse 1s infinite',
                animationDelay: '0.2s'
              }}></div>
              <div className="loading-dot" style={{
                width: '6px',
                height: '6px',
                backgroundColor: isFollowingCachedLine && movesMade > 0 ? recalculateButtonColor : '#4CAF50',
                borderRadius: '50%',
                animation: 'pulse 1s infinite',
                animationDelay: '0.4s'
              }}></div>
            </div>
          </div>
        </div>
      );
    }

    // If no analysis data yet, show prompt
    if (!isUsingRealData) {
      // Check if we're currently analyzing or if we just don't have data yet
      if (isEvaluating && shouldRefreshUI) {
        return (
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            width: '100%',
            justifyContent: 'center',
            alignItems: 'center',
            padding: '20px',
            textAlign: 'center',
            opacity: 0.8
          }}>
            <i className="fa-solid fa-chess-knight" style={{ fontSize: '24px', marginBottom: '10px', color: '#4CAF50' }}></i>
            <p>Calculating...</p>
          </div>
        );
      } else if (isAnalyzing) {
        // If analysis is active but we don't have converted PVs yet
        return (
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            width: '100%',
            justifyContent: 'center',
            alignItems: 'center',
            padding: '20px',
            textAlign: 'center',
            opacity: 0.8
          }}>
            <i className="fa-solid fa-chess-knight" style={{ fontSize: '24px', marginBottom: '10px', color: '#4CAF50' }}></i>
            <p>Waiting for Stockfish data...</p>
          </div>
        );
      } else {
        return (
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            width: '100%',
            justifyContent: 'center',
            alignItems: 'center',
            padding: '20px',
            textAlign: 'center',
            opacity: 0.8
          }}>
            <i className="fa-solid fa-chess-knight" style={{ fontSize: '24px', marginBottom: '10px', color: '#4CAF50' }}></i>
            <p>Click Recalculate to run Stockfish analysis for this position</p>
          </div>
        );
      }
    }

    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        gap: '10px',
      }}>
        {/* Use shared FullLineDisplay component without back button */}
        {pvData && <FullLineDisplay
          lineData={pvData}
          lastBoardMoveSAN={lastBoardMoveSAN}
          setLastBoardMoveSAN={setLastBoardMoveSAN}
          analysisStartTurn={analysisStartTurn} // +++ Pass prop +++
        />}
      </div>
    );
  };

  // Reset the current move index when changing UI versions or lines
  useEffect(() => {
    setCurrentMoveIndex(-1);
  }, [uiVersion, activeLine, expandedLine]);

  // Add a function to help validate moves and expose it to the window object
  useEffect(() => {
    // Create a function to verify if a move is legal from the current position
    window.isLegalMove = (moveStr) => {
      try {
        // Get the current FEN
        const fen = window.getCurrentBoardPosition ? window.getCurrentBoardPosition() : getCurrentFEN();
        if (!fen) return true; // If we can't determine the position, assume legal

        // Create a chess.js instance with the current position
        const chess = new Chess(fen);

        // Try to make the move and see if it's valid
        const result = chess.move(moveStr, { sloppy: true });
        return result !== null;
      } catch (error) {
        return true; // If any error occurs, assume the move is legal
      }
    };

    return () => {
      // Clean up the window function when component unmounts
      delete window.isLegalMove;
    };
  }, [getCurrentFEN]);

  // +++ Effect to handle Puzzle Mode UI changes (runs only once initially) +++
  useEffect(() => {
    // Only run if isPuzzleMode is true, we have PVs, AND setup hasn't been done yet
    if (isPuzzleMode && convertedPVs?.length > 0 && !puzzleSetupDoneRef.current) {

      setUiVersion('1');
      setExpandedLine(1);
      setActiveLine(1);
      setActiveTab('main');
      setCurrentMoveIndex(-1);
      setMovesMade(0);

      // Set the flag to prevent re-running this setup logic
      puzzleSetupDoneRef.current = true;

      // Logic from updateBestMoveArrow to show the first move arrow immediately
      if (arrowSetting !== 'NONE') {
        const firstLine = convertedPVs[0];
        if (firstLine && firstLine.bestMove) {
          const arrowData = formatStockfishArrow(firstLine.bestMove, 1);
          if (arrowData) {

            setStockfishArrows([arrowData]);
          } else {
            setStockfishArrows([]);
          }
        } else {
          setStockfishArrows([]);
        }
      } else {
        setStockfishArrows([]); // Clear arrows if setting is NONE
      }
    }

    // Reset the flag if puzzle mode is turned off
    if (!isPuzzleMode && puzzleSetupDoneRef.current) {

      puzzleSetupDoneRef.current = false;
    }
    // Dependencies: Only need isPuzzleMode and convertedPVs to trigger the check.
    // Keep arrowSetting, setStockfishArrows, formatStockfishArrow for the arrow logic.
  }, [isPuzzleMode, convertedPVs, arrowSetting, setStockfishArrows, formatStockfishArrow]); // Reduced dependencies
  // +++ End Puzzle Mode Effect +++

  // Reset cache when component unmounts
  useEffect(() => {
    return () => {
      resetCache();
      // Reset Modern UI state tracking
      setSelectedLineIndex(null);
      setCurrentMoveDisplayIndex(0);
      setMoveHistory([]);
    };
  }, []);

  // Handle recalculation threshold change
  const handleThresholdChange = (e) => {
    const value = parseInt(e.target.value);
    if (!isNaN(value) && value >= 1 && value <= 8) {
      setRecalcThreshold(value);
      setRecalculationThreshold(value);
      // Save to localStorage
      localStorage.setItem('stockfishRecalcThreshold', value.toString());
    }
  };

  // Handle analysis depth change
  const handleDepthChange = (e) => {
    const value = parseInt(e.target.value);
    updateAnalysisDepth(value);
  };

  // Helper function to update analysis depth, handling bounds and saving
  const updateAnalysisDepth = (newValue) => {
    const value = parseInt(newValue);
    if (!isNaN(value) && value >= 5 && value <= 20) { // Use constants or ensure these match input attributes
      setAnalysisDepth(value);
      // Save to localStorage
      localStorage.setItem('stockfishAnalysisDepth', value.toString());
    }
  };

  // Handlers for the new +/- buttons
  const handleDecrementDepth = () => {
    updateAnalysisDepth(analysisDepth - 1);
  };

  const handleIncrementDepth = () => {
    updateAnalysisDepth(analysisDepth + 1);
  };

  // Effect to sync currentPosition with currentBoardFEN
  useEffect(() => {
    if (currentPosition) {
      setCurrentBoardFEN(currentPosition);
    }
  }, [currentPosition]);

  // Effect to log when the current board FEN changes
  useEffect(() => {
    if (currentBoardFEN) {
    }
  }, [currentBoardFEN]);

  // Add a ref to track if auto-calculation is in progress
  const isCalculationInProgressRef = useRef(false);

  // Auto-calculate on position changes
  useEffect(() => {
    // Skip the initial render or if engine isn't ready
    if (!currentPosition || !engineReady) return;

    // Skip if we're already evaluating to avoid duplicate calculations
    if (isEvaluating || isCalculationInProgressRef.current) {
      
      return;
    }

    // Skip if the user hasn't manually analyzed yet (except for background calculation)
    // Allow background calculation for evaluation bar even without manual analysis
    if (!hasManuallyAnalyzed && !isViewGame) {
      // Enable background calculation for evaluation purposes
      // console.log('Stockfish.js: Enabling background calculation for evaluation bar');
    }

    // Get the current move index to check for N+1 data
    const currentMoveIdx = typeof window.getCurrentMoveIndex === 'function' ?
      window.getCurrentMoveIndex() : null;

    // console.log('Stockfish.js: Auto-calculate effect triggered', {
    //   position: currentPosition,
    //   currentMoveIdx,
    //   inPositionsMap: positionsWithAnalysis[currentPosition] ? true : false,
    //   inWindowData: window.moveClassificationData?.positionFEN === currentPosition
    // });

    // Check if N+1 move has analysis data for this position
    let hasNextMoveAnalysis = false;

    if (currentMoveIdx !== null && typeof window.getMove === 'function' && window.moves) {
      try {
        // Calculate the next move index
        const nextMoveIdx = parseInt(currentMoveIdx) + 1;

        // Check if next move exists and has analysis data
        if (nextMoveIdx < window.moves.length) {
          const nextMoveData = window.getMove(window.moves, nextMoveIdx.toString());

          if (nextMoveData && nextMoveData.analysis) {
            

            // Get position FEN from the analysis if available
            const analysisFEN = nextMoveData.analysis.positionFEN || currentPosition;

            // Compare FENs but ignore the move counters at the end
            const currentPositionBase = currentPosition.split(' ').slice(0, 4).join(' ');
            const analysisFENBase = analysisFEN.split(' ').slice(0, 4).join(' ');

            // console.log('Stockfish.js: Auto-calc comparing N+1 analysis position with current', {
            //   currentPositionBase,
            //   analysisFENBase,
            //   isMatch: currentPositionBase === analysisFENBase
            // });

            // Only use the analysis if it's for the same position
            if (currentPositionBase === analysisFENBase) {
              hasNextMoveAnalysis = true;

              // Update the window cache objects from the move data
              window.currentAnalysisCache = nextMoveData.analysis;

              // Update classification data if available
              if (nextMoveData.analysis.classification) {
                window.moveClassificationData = {
                  classification: nextMoveData.analysis.classification,
                  positionFEN: currentPosition,
                  timestamp: Date.now()
                };
              }

              // Update the PVs with cached data
              if (nextMoveData.analysis.pvs && nextMoveData.analysis.pvs.length > 0) {
                setConvertedPVs(nextMoveData.analysis.pvs);
              }

              // Update evaluation if available
              if (nextMoveData.analysis.scores && nextMoveData.analysis.scores.length > 0) {
                setEvaluation(nextMoveData.analysis.scores[0]);
                if (setGlobalEvaluation) {
                  setGlobalEvaluation(nextMoveData.analysis.scores[0]);
                }
              }

              // No need to run analysis - we already have the data
              return;
            } else {
              
            }
          }
        }

        // Check current move as fallback
        const moveData = window.getMove(window.moves, currentMoveIdx);

        if (moveData && moveData.analysis) {
          

          // Get position FEN from the analysis if available
          const analysisFEN = moveData.analysis.positionFEN || currentPosition;

          // Compare FENs but ignore the move counters at the end
          const currentPositionBase = currentPosition.split(' ').slice(0, 4).join(' ');
          const analysisFENBase = analysisFEN.split(' ').slice(0, 4).join(' ');

          // Only use the analysis if it's for the same position
          if (currentPositionBase === analysisFENBase) {
            hasNextMoveAnalysis = true;

            // Update the window cache objects from the move data
            window.currentAnalysisCache = moveData.analysis;

            // Update classification data if available
            if (moveData.analysis.classification) {
              window.moveClassificationData = {
                classification: moveData.analysis.classification,
                positionFEN: currentPosition,
                timestamp: Date.now()
              };
            }

            // Update the PVs with cached data
            if (moveData.analysis.pvs && moveData.analysis.pvs.length > 0) {
              setConvertedPVs(moveData.analysis.pvs);
            }

            // Update evaluation if available
            if (moveData.analysis.scores && moveData.analysis.scores.length > 0) {
              setEvaluation(moveData.analysis.scores[0]);
              if (setGlobalEvaluation) {
                setGlobalEvaluation(moveData.analysis.scores[0]);
              }
            }

            // No need to run analysis - we already have the data
            return;
          } else {
            
          }
        }
      } catch (error) {
        console.error('Stockfish.js: Error checking N+1 move for analysis:', error);
      }
    }

    // Check both our position mapping and window data
    const hasExistingData = positionsWithAnalysis[currentPosition] ||
      window.moveClassificationData?.positionFEN === currentPosition;

    // Skip if we already have analysis for this position
    if (hasExistingData) {
      

      // Still update the UI state to show we have data
      if (window.currentAnalysisCache?.pvs) {
        setConvertedPVs(window.currentAnalysisCache.pvs);
      }

      if (window.currentAnalysisCache?.scores && window.currentAnalysisCache.scores.length > 0) {
        setEvaluation(window.currentAnalysisCache.scores[0]);
        if (setGlobalEvaluation) {
          setGlobalEvaluation(window.currentAnalysisCache.scores[0]);
        }
      }

      return;
    }

    // Only recalculate if this is a new position
    if (lastAnalyzedPositionRef.current !== currentPosition) {
      
      lastAnalyzedPositionRef.current = currentPosition;

      // Set flag to prevent multiple concurrent calculations
      isCalculationInProgressRef.current = true;

      // Start analysis with the current position
      // Use small timeout to allow the board to update fully
      setTimeout(() => {
        // Final check before initiating analysis
        if (positionsWithAnalysis[currentPosition] ||
          window.moveClassificationData?.positionFEN === currentPosition) {
          
          isCalculationInProgressRef.current = false;
          return;
        }

        // Set analyzing state to true
        setIsAnalyzing(true);
        setContextIsAnalyzing(true);
        setIsEvaluating(true);

        // Reset cache-related state
        setIsFollowingCachedLine(false);
        setFollowingLineIndex(-1);
        setMovesMade(0);
        setUseCachedEvaluation(false);

        // Update the original analysis position
        setOriginalAnalysisFEN(currentPosition);

        // Directly analyze position to avoid circular dependency
        stockfishEngine.analyzePosition(currentPosition, {
          depth: analysisDepth,
          multipv: 3
        }).then(() => {
          // Clear flag when analysis completes
          isCalculationInProgressRef.current = false;
        }).catch(error => {
          console.error('Error in auto-calculation:', error);
          setIsEvaluating(false);
          // Clear flag on error too
          isCalculationInProgressRef.current = false;
        });
      }, 50); // Reduced from 300ms to 50ms
    }
  }, [currentPosition, engineReady, isEvaluating, positionsWithAnalysis, isViewGame, hasManuallyAnalyzed]);

  // Effect to handle Stockfish evaluation and update UI accordingly
  useEffect(() => {
    if (!engineReady || !isAnalyzing) return;

    let isMounted = true;

    const listener = stockfishEngine.onMessage(data => {
      if (!isMounted) return;

      // Process the evaluation data
      if (data.type === 'evaluation') {
        // Convert depth info
        const convertedInfo = data.data;

        // For global access to the latest evaluation
        window.latestEvaluation = {
          ...convertedInfo,
          bestMove: convertedInfo.bestMove?.san || null
        };

        // Log the evaluation data being stored for debugging
        // console.log('[DEBUG] Storing Stockfish evaluation on window.latestEvaluation:', {
        //   hasEvaluation: !!convertedInfo,
        //   bestMove: convertedInfo.bestMove?.san || null,
        //   depth: convertedInfo.depth,
        //   hasPVs: !!(convertedInfo.pvs && convertedInfo.pvs.length),
        //   multiPVCount: convertedInfo.pvs?.length || 0
        // });

        // Update global evaluation in context - this triggers UI refresh for all components
        setGlobalEvaluation(convertedInfo);

        // Add best lines to PV list
        if (convertedInfo.pvs && convertedInfo.pvs.length > 0) {
          // Format PVs
          const lines = [];

          convertedInfo.pvs.forEach((pv, idx) => {
            if (!pv || !pv.moves || pv.moves.length === 0) return;

            try {
              const sanMoves = pv.moves.map(m => m.san || m).filter(Boolean);

              // Convert LAN to SAN if needed
              const formattedMoves = convertLANtoSAN(sanMoves);

              lines.push({
                eval: pv.eval || 0,
                cp: pv.cp,
                mate: pv.mate,
                moves: formattedMoves,
                raw: pv.raw || '',
                depth: convertedInfo.depth
              });
            } catch (err) {
              console.error('Error parsing line:', err);
            }
          });

          if (lines.length > 0) {
            setConvertedPVs(lines);
            setIsEvaluating(false);

            // Once analysis is complete, update positionsWithAnalysis to prevent reanalysis
            const currentFEN = getCurrentFEN() || currentPosition;
            if (currentFEN) {
              // Update our mapping to mark this position as analyzed
              
              window.moveClassificationData = {
                classification: 'calculationComplete',  // Generic classification placeholder
                positionFEN: currentFEN,
                timestamp: Date.now()
              };

              // Create a cache object if window.currentAnalysisCache doesn't exist
              if (!window.currentAnalysisCache) {
                window.currentAnalysisCache = {
                  pvs: lines,
                  scores: [],
                  depth: analysisDepth  // Include the depth at which analysis was performed
                };
              }
            }
          }
        }
      }
    });

    return () => {
      isMounted = false;
      if (listener && typeof listener === 'function') {
        listener(); // Remove the listener
      }
    };
  }, [engineReady, isAnalyzing, setGlobalEvaluation, convertLANtoSAN, currentPosition, getCurrentFEN]);

  // If showStockfish is false, display the ENGINE OFF message
  // if (!showStockfish) {
  //   return (
  //     <div
  //       style={{
  //         background: 'black',
  //         color: 'gold',
  //         width: '100%',
  //         height: '100%',
  //         display: 'flex',
  //         justifyContent: 'center',
  //         alignItems: 'center',
  //         fontStyle: 'italic',
  //         fontSize: '24px',
  //         fontWeight: 'bold',
  //         padding: '20px',
  //       }}
  //     >
  //       ENGINE OFF
  //     </div>
  //   );
  // }

  // Helper function to analyze a single position and return a Promise
  const analyzeSinglePosition = useCallback(async (fen, depth) => {
    

    // +++ Add detailed inspection logs +++
    
    // Use optional chaining for safety
    
    // +++ End detailed inspection logs +++

    if (!engineReady || !stockfishEngine) {
      throw new Error("Stockfish engine not ready.");
    }

    return new Promise(async (resolve, reject) => {
      let analysisData = { pvs: [], scores: [], bestMove: null, depth: depth };
      let bestMoveStr = null;
      let collectedPVs = []; // Temporary storage for PVs
      let maxDepthReached = 0; // Track the maximum depth reached

      // --- Temporary Message Listener ---
      const temporaryListener = stockfishEngine.onMessage(message => {
        // <-- Log raw messages

        // --- Replicated Parsing Logic --- 
        if (message.includes('info depth') && message.includes('multipv')) {
          try {
            // Extract multipv number to identify the line
            const multipvMatch = message.match(/multipv (\d+)/); // Corrected regex
            const lineNumber = multipvMatch ? parseInt(multipvMatch[1]) : 1;

            // Extract depth
            const depthMatch = message.match(/depth (\d+)/); // Corrected regex
            const currentDepth = depthMatch ? parseInt(depthMatch[1]) : 0;

            // Update maximum depth reached
            maxDepthReached = Math.max(maxDepthReached, currentDepth);

            // Determine whose turn it is from the *passed FEN*
            const isBlackToMove = fen && fen.split(' ')[1] === 'b';

            // Log the depth comparison
            

            // Process all depths, not just target depth (we'll use the deepest one)
            // if (currentDepth === depth) {
            // Extract evaluation
            let evaluation;
            if (message.includes('score cp')) {
              const cpMatch = message.match(/score cp (-?\d+)/);
              if (cpMatch) {
                let cpScore = parseInt(cpMatch[1]) / 100; // Convert centipawns to pawns
                if (isBlackToMove) {
                  cpScore = -cpScore;
                }
                evaluation = cpScore >= 0 ? `+${cpScore.toFixed(2)}` : cpScore.toFixed(2);
              }
            } else if (message.includes('score mate')) {
              const mateMatch = message.match(/score mate (-?\d+)/);
              if (mateMatch) {
                let mateIn = parseInt(mateMatch[1]);
                if (isBlackToMove) {
                  mateIn = -mateIn;
                }
                evaluation = `M${mateIn}`;
              }
            }

            

            // Extract PV string
            const pvKeywordIndex = message.indexOf(' pv ');
            if (pvKeywordIndex !== -1 && evaluation !== null) {
              const pvString = message.substring(pvKeywordIndex + 4).trim();
              const pvMovesLAN = pvString.split(' ');
              const bestMoveLAN = pvMovesLAN[0];

              if (bestMoveLAN && bestMoveLAN.length >= 4) {
                // Convert the moves to SAN notation using the *passed FEN*
                const sanMoves = convertLANtoSAN(pvMovesLAN, fen);

                // Only update the PV if this depth is >= the previous depth for this line
                const existingDepth = collectedPVs[lineNumber - 1]?.depth || 0;
                if (currentDepth >= existingDepth) {
                  // Update the *local* collectedPVs array for this specific analysis
                  collectedPVs[lineNumber - 1] = {
                    line: lineNumber,
                    evaluation: evaluation,
                    bestMove: bestMoveLAN,
                    moveName: sanMoves[0] || "",
                    lanMoves: pvMovesLAN,
                    fullLine: sanMoves.join(' '),
                    moves: sanMoves,
                    depth: currentDepth // Use the depth from the message
                  };
                }
              } else {
                console.warn(`[analyzeSinglePosition] Invalid bestMove extracted ('${bestMoveLAN}') or missing startFen. Skipping PV update for line ${lineNumber}.`);
              }
            }
            // } // End if (currentDepth === depth)
          } catch (error) {
            console.error('[analyzeSinglePosition] Error parsing engine output:', error, message);
          }
        }
        // --- End Replicated Parsing Logic ---

        // Check for bestmove to resolve the promise
        if (message.includes('bestmove')) {
          const match = message.match(/bestmove ([a-h][1-8][a-h][1-8][qbnr]?)/);
          if (match) {
            bestMoveStr = match[1];
          }

          // Ensure each PV has the exact required structure before resolving
          // This matches the format seen in the database screenshot
          const formattedPVs = collectedPVs.filter(Boolean).map(pv => {
            // Ensure each PV has all required fields in the expected format
            return {
              bestMove: pv.bestMove, // LAN format e.g., "e2e4"
              evaluation: pv.evaluation, // String format e.g., "+0.17"
              moveName: pv.moveName, // SAN format e.g., "e4"
              moves: pv.moves || [], // Array of SAN moves
              line: pv.line // Line number (multipv index)
            };
          });

          // Consolidate results with the correct format
          analysisData.pvs = formattedPVs;
          analysisData.scores = formattedPVs.map(pv => pv.evaluation);
          analysisData.bestMove = bestMoveStr;

          // Record the actually achieved depth (might be lower than target)
          analysisData.depth = maxDepthReached;
          

          // Remove the listener
          temporaryListener(); // Call the remover function returned by onMessage
          resolve(analysisData);
        }
      });
      // --- End Temporary Listener ---

      try {
        // Stop any previous analysis
        // <-- Add log
        // <-- Add this log
        // await stockfishEngine.stop(); // Original failing line
        await stockfishEngine.stopAnalysis(); // <-- Try using stopAnalysis instead
        // <-- Log success

        // Set position and start analysis
        await stockfishEngine.analyzePosition(fen, { depth, multipv: 3 });
      } catch (engineError) {
        console.error('Error during analyzeSinglePosition:', engineError);
        temporaryListener(); // Clean up listener on error
        reject(engineError);
      }
    });
  }, [engineReady, convertLANtoSAN]); // Add dependencies


  // +++ Full Game Analysis Handler +++
  const handleFullGameAnalysis = useCallback(async () => {
    
    if (isFullAnalysisRunning) {
      
      return;
    }

    // +++ Define variables early +++
    const targetDepth = 15; // Target analysis depth
    const gameMoves = window.moves; // Get moves from global scope (adjust if needed)

    if (!gameMoves || gameMoves.length === 0) {
      setAnalysisError("No moves found to analyze.");
      setIsFullAnalysisRunning(false);
      return;
    }
    const totalMoves = gameMoves.length;
    // +++ End define variables early +++

    

    setIsFullAnalysisRunning(true);
    setFullAnalysisProgress(0);
    setFullAnalysisCurrentMove(0);
    setAnalysisError(null); // Clear previous errors

    // --- Variables moved up ---
    // const targetDepth = 20; // Target analysis depth
    // const gameMoves = window.moves; // Get moves from global scope (adjust if needed)
    // ... (check for gameMoves moved up too)
    // const totalMoves = gameMoves.length;
    // --- End Variables moved up ---

    const chess = new Chess(); // Start from initial position

    

    let previousAnalysisResult = null; // Store the previous move's analysis result

    try {
      for (let i = 0; i < totalMoves; i++) {
        setFullAnalysisCurrentMove(i + 1); // Update progress indicator (1-based index)
        const currentMoveData = gameMoves[i];
        const fenToAnalyze = chess.fen(); // FEN *before* making move 'i'
        const sideToMove = fenToAnalyze.split(' ')[1]; // 'w' or 'b'

        

        // Check if existing analysis meets the depth requirement
        let currentAnalysisResult;
        const existingAnalysis = currentMoveData.analysis || currentMoveData.stockfish; // Check both possible locations
        if (existingAnalysis && existingAnalysis.depth && existingAnalysis.depth >= targetDepth) {
          
          currentAnalysisResult = existingAnalysis; // Use existing data for classification check
          // Make the move on the board to advance state
          const moveResult = chess.move(currentMoveData.notation);
          if (!moveResult) {
            console.warn(`Could not make move ${currentMoveData.notation} on internal board. Skipping rest.`);
            throw new Error(`Invalid move sequence at move ${i + 1}`);
          }
          setFullAnalysisProgress(Math.round(((i + 1) / totalMoves) * 100));

          // Add detailed log for skipped moves
          // console.log(`MOVE ${i + 1}/${totalMoves} (${currentMoveData.notation}): SKIPPED - Using existing analysis:`, {
          //   depth: existingAnalysis.depth,
          //   scores: existingAnalysis.scores,
          //   pvs: existingAnalysis.pvs ? existingAnalysis.pvs.length : 0,
          //   classification: existingAnalysis.classification
          // });

          continue; // Skip to next move
        }

        

        // Analyze the position
        currentAnalysisResult = await analyzeSinglePosition(fenToAnalyze, targetDepth);
        // Existing log
        // <-- Add requested log

        // --- Calculate Classification --- 
        let classification = null;
        if (previousAnalysisResult && currentAnalysisResult) {
          const previousEval = previousAnalysisResult.scores?.[0] || null;
          const currentEval = currentAnalysisResult.scores?.[0] || null;
          const previousFEN = previousAnalysisResult.positionFEN || null;

          // Need the FEN *after* the current move for the classifier's currentFEN
          // We simulate making the move to get the resulting FEN
          const tempChess = new Chess(fenToAnalyze);
          const moveMade = tempChess.move(currentMoveData.notation);
          const fenAfterMove = moveMade ? tempChess.fen() : null;

          if (fenAfterMove) { // Only classify if the move was valid
            classification = calculateMoveClassification(
              previousEval,
              currentEval,
              sideToMove, // Side who made the move we are classifying
              fenToAnalyze, // Position *before* the move
              fenAfterMove // Position *after* the move
            );
            
          } else {
            console.warn(`Could not simulate move ${currentMoveData.notation} for classification.`);
          }
        }
        // --- End Calculate Classification ---

        // Save the result to the current move's analysis object
        // Format the data consistently with saveAnalysisToMove function

        // First, ensure the move object has all required properties
        if (!gameMoves[i]) {
          console.error(`Cannot save analysis: Move at index ${i} does not exist`);
          continue;
        }

        // Format the analysis object to match the expected structure in the database
        gameMoves[i] = {
          ...gameMoves[i],
          analysis: {
            pvs: currentAnalysisResult.pvs || [],           // Array of principal variations
            scores: currentAnalysisResult.scores || [],     // Array of evaluation scores as strings
            classification: classification,                 // The calculated move classification
            depth: currentAnalysisResult.depth              // Additional: record the achieved depth
          }
        };

        

        // Add detailed log for analyzed moves
        // console.log(`MOVE ${i + 1}/${totalMoves} (${currentMoveData.notation}): ANALYZED - Result:`, {
        //   depth: gameMoves[i].analysis.depth,
        //   scores: gameMoves[i].analysis.scores,
        //   pvs: gameMoves[i].analysis.pvs ? gameMoves[i].analysis.pvs.length : 0,
        //   bestMove: currentAnalysisResult.bestMove,
        //   classification: gameMoves[i].analysis.classification,
        //   positionFEN: fenToAnalyze
        // });

        // Update previous analysis result for the next iteration
        previousAnalysisResult = {
          ...currentAnalysisResult,
          positionFEN: fenToAnalyze,
          classification: classification
        };

        // Make the move on the internal board to proceed (if not already made in skip block)
        if (!(existingAnalysis && existingAnalysis.depth && existingAnalysis.depth >= targetDepth)) {
          const moveResult = chess.move(currentMoveData.notation);
          if (!moveResult) {
            console.warn(`Could not make move ${currentMoveData.notation} on internal board after analysis. Skipping rest.`);
            throw new Error(`Invalid move sequence at move ${i + 1}`);
          }
        }

        // Update progress
        setFullAnalysisProgress(Math.round(((i + 1) / totalMoves) * 100));

        // Optional: Short delay between analyses to prevent overwhelming the system/UI
        await new Promise(resolve => setTimeout(resolve, 100));
      }

      

      // Validate the final analysis data before notifying parent
      let validAnalysisCount = 0;
      let invalidMoves = [];

      for (let i = 0; i < gameMoves.length; i++) {
        const move = gameMoves[i];
        if (move && move.analysis &&
          Array.isArray(move.analysis.pvs) &&
          Array.isArray(move.analysis.scores) &&
          move.analysis.classification) {
          validAnalysisCount++;
        } else {
          invalidMoves.push(i); // Track any moves with missing/invalid analysis
        }
      }

      
      if (invalidMoves.length > 0) {
        console.warn(`Some moves don't have valid analysis data: ${invalidMoves.join(', ')}`);
      }

      // Optionally, trigger a save mechanism here if needed (e.g., if AddGame needs to know)
      if (window.onFullAnalysisComplete) {
        window.onFullAnalysisComplete(gameMoves);
      }

    } catch (error) {
      console.error("Error during full game analysis:", error);
      setAnalysisError(`Error during analysis: ${error.message}`);
    } finally {
      setIsFullAnalysisRunning(false);
      setFullAnalysisProgress(0);
      setFullAnalysisCurrentMove(0);
    }
  }, [isFullAnalysisRunning, analyzeSinglePosition]); // Add dependencies

  // Render the regular Stockfish component if showStockfish is true
  return (
    <div style={{ /* Main container styles */
      border: '5px solid black',
      borderRadius: '5px',
      background: colors.background,
      overflow: 'hidden',
      boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
      display: 'flex',
      flexDirection: 'column',
      minHeight: '125px',
      height: '140px',
      position: 'relative', // Add relative positioning to parent
    }}>

      {/* --- Header (Visibility logic unchanged) --- */}
      <div style={{
        visibility: !(activeTab === 'main' && uiVersion === '1' && expandedLine === null) ? 'visible' : 'hidden',
        height: !(activeTab === 'main' && uiVersion === '1' && expandedLine === null) ? '70px' : '0px',
        // ... other header styles ...
        padding: '5px 10px', // Consistent padding when visible
        background: 'black',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between', // Added this
        gap: '15px',
        position: 'sticky',
        top: 0,
        zIndex: 10,
      }}>
        {/* --- Left Group: Gear/Undo + Conditional Button/Title --- */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          {/* Back arrow (conditionally rendered) */}
          {activeTab === 'main' && uiVersion === '1' && expandedLine !== null && (
            <i
              className="fa-solid fa-arrow-left"
              style={{
                color: 'black', // Use white
                cursor: 'pointer',
                fontSize: '20px',
                borderRadius: '50%',
                background: '#ff4444',
                padding: '5px',
              }}
              onClick={() => setExpandedLine(null)} // Go back action
              title="Back to Top Moves"
            ></i>
          )}
          <i
            className={activeTab === 'settings' ? "fa-solid fa-circle-xmark" : "fa-solid fa-gear"}
            style={{
              cursor: 'pointer',
              fontSize: '28px',
              color: activeTab === 'settings' ? '#ff4444' : 'white',
              transition: 'color 0.3s ease',
              marginRight: '10px',
              marginLeft: activeTab === 'settings' ? '0px' : '10px',
              // Add margin ONLY when back arrow is also visible
            }}
            onClick={handleTabSwitch} // Single click handler
          ></i>
          {/* ... Other header elements like Stockfish Title, only rendered when header is visible ... */}
          {activeTab === 'main' && expandedLine !== null && isEvaluating && ( /* Example: Top Moves button for expanded */
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', color: 'gold', fontWeight: 'bold', fontSize: '20px', fontStyle: 'italic' }}>Calculating...<i className="fa-solid fa-chess-knight fa-bounce" style={{ cursor: 'pointer', fontSize: '24px', color: '', transition: 'color 0.3s ease', }} ></i>

            </div>

          )}
          {activeTab === 'settings' && ( /* Example: Stockfish Title for settings */
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              borderRadius: '8px',

              transition: 'all 0.3s ease',
              minWidth: '125px',
            }}>
              <div style={{ width: '12px', height: '12px', borderRadius: '50%', background: '#4CAF50', boxShadow: '0 0 8px 2px rgba(76, 175, 80, 0.4)', transition: 'all 0.3s ease', }} />
              <span style={{ color: 'rgba(76, 175, 80)', fontWeight: 'bold', fontSize: '18px', fontStyle: 'italic', }}>Stockfish 16</span>
              <span style={{ color: 'white', fontSize: '14px', fontStyle: 'italic' }}>- Running in local browser on 32 megabyte RAM.</span>
            </div>
          )}
        </div>

        {/* --- Right Group: Line Navigation (Only when header visible and conditions met) --- */}
        {activeTab === 'main' && (uiVersion === '2' || (uiVersion === '1' && expandedLine !== null)) && (
          <div style={{ display: 'flex', gap: '5px' /* Removed marginLeft: 'auto' */ }}> {/* Nav buttons container */}
            <button
              onClick={() => handleNavigateLine('prev')}
              disabled={!convertedPVs || convertedPVs.length === 0 || activeLine === 1}
              style={{ /* existing prev button styles */
                padding: '6px 10px',
                borderRadius: '5px',
                border: '3px solid rgb(26, 38, 53)',
                cursor: (!convertedPVs || convertedPVs.length === 0 || activeLine === 1) ? 'default' : 'pointer',
                background: 'black',
                fontWeight: 'bold',
                opacity: (!convertedPVs || convertedPVs.length === 0 || activeLine === 1) ? 0.5 : 1,
                border: activeLine === 2 ? `2px solid gold` : '2px solid rgb(76, 175, 80)',
                color: activeLine === 2 ? 'gold' : 'rgb(76, 175, 80)',
                visibility: activeLine === 1 ? 'hidden' : 'visible',
              }}
            >
              <span style={{ marginRight: '5px' }}><i className="fa-solid fa-chevron-left"></i></span>
              Line {activeLine > 1 ? activeLine - 1 : 1}
            </button>
            <button
              onClick={() => handleNavigateLine('next')}
              disabled={!convertedPVs || convertedPVs.length === 0 || activeLine === (convertedPVs?.length || 3)}
              style={{ /* existing next button styles */
                padding: '6px 10px',
                borderRadius: '5px',
                cursor: (!convertedPVs || convertedPVs.length === 0 || activeLine === (convertedPVs?.length || 3)) ? 'default' : 'pointer',
                background: 'black',
                fontWeight: 'bold',
                visibility: activeLine === (convertedPVs?.length || 3) ? 'hidden' : 'visible',
                border: activeLine === 2 ? `2px solid #42a5f5` : '2px solid rgb(76, 175, 80)',
                color: activeLine === 2 ? '#42a5f5' : 'rgb(76, 175, 80)',
              }}
            >
              Line {activeLine < (convertedPVs?.length || 3) ? activeLine + 1 : (convertedPVs?.length || 3)}
              <span style={{ marginLeft: '5px' }}><i className="fa-solid fa-chevron-right"></i></span>
            </button>
          </div>
        )}
        {/* ... Potentially other header elements ... */}
      </div>

      {/* --- Updated Main Content Area --- */}
      <div style={{
        height: '100%', // Adjusted height calculation (was calc(100% - 50px))
        padding: activeTab === 'main' && uiVersion === '1' && expandedLine === null ? '0px 15px' : '0px 0px', // Consistent padding
        color: 'white',
        background: colors.background,
        zIndex: 1,

        display: 'flex', // Use flex for layout
        // --- Conditional Layout ---
        // Default: Center content when not analyzing, align start otherwise
        // justifyContent: (activeTab === 'main' && uiVersion === '1' && expandedLine === null)
        //   ? 'flex-start' // Align left for collapsed modern view
        //   : (isAnalyzing ? 'flex-start' : 'center'),
        // alignItems: (activeTab === 'main' && uiVersion === '1' && expandedLine === null)
        //   ? 'center' // Align items center vertically for collapsed modern view
        //   : (isAnalyzing ? 'flex-start' : 'center'),
        gap: (activeTab === 'main' && uiVersion === '1' && expandedLine === null) ? '20px' : '0', // Add gap for collapsed view columns
      }}>

        {/* --- Conditional Left Column (Gear/Loader for Collapsed Modern View) --- */}
        {activeTab === 'main' && uiVersion === '1' && expandedLine === null && (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '20px', minWidth: '25px', maxWidth: '25px' }}>

            {/* Bouncing Knight Loader (only when loading) */}
            {isEvaluating && shouldRefreshUI && (
              <div style={{ /* Loader styles */
                color: '#ffd700',
                fontWeight: 'bold',
                fontSize: '24px',
                fontStyle: 'italic',
              }}>
                <i className="fa-solid fa-chess-knight fa-bounce"></i>
              </div>
            )}

            {/* --- Full Game Analysis Button --- */}
            <div>
              {!isViewGame && (
                <div style={{ position: 'relative' }}>
                  <button
                    onClick={() => {
                      handleFullGameAnalysis(); // Call the actual handler
                    }}
                    disabled={isFullAnalysisRunning} // Disable while running
                    style={{
                      background: 'none',
                      border: 'none',
                      color: isFullAnalysisRunning ? '#4CAF50' : 'grey', // Green when running, grey otherwise
                      cursor: isFullAnalysisRunning ? 'default' : 'pointer',
                      fontSize: '24px',
                      color: 'gold',
                      transition: 'color 0.3s ease',
                      ':hover': {
                        color: isFullAnalysisRunning ? '#4CAF50' : '#6fcc73', // Lighter green on hover when enabled
                      }
                    }}
                    onMouseEnter={(e) => {
                      const tooltip = document.getElementById('tooltip-fullgame');
                      if (tooltip) {
                        tooltip.style.visibility = 'visible';
                        tooltip.style.opacity = '1';

                        // Position tooltip relative to button
                        const rect = e.currentTarget.getBoundingClientRect();
                        tooltip.style.left = `${rect.left + rect.width / 2}px`;
                        tooltip.style.top = `${rect.bottom + 10}px`;
                        tooltip.style.transform = 'translateX(-50%)';
                      }
                    }}
                    onMouseLeave={() => {
                      const tooltip = document.getElementById('tooltip-fullgame');
                      if (tooltip) {
                        tooltip.style.visibility = 'hidden';
                        tooltip.style.opacity = '0';
                      }
                    }}
                  >
                    {/* Change icon when running */}
                    {!isEvaluating && !shouldRefreshUI && (
                      <i className={`fa-solid ${isFullAnalysisRunning ? 'fa-spinner fa-spin' : 'fa-chess-knight'}`}></i>)}

                    {/* Custom tooltip */}
                    <span
                      id="tooltip-fullgame"
                      style={{
                        visibility: 'hidden',
                        position: 'fixed',
                        backgroundColor: 'black',
                        color: 'gold',
                        textAlign: 'center',
                        padding: '6px 12px',
                        borderRadius: '6px',
                        zIndex: 1500,
                        whiteSpace: 'nowrap',
                        border: '2px solid gold',
                        fontWeight: 'bold',
                        fontSize: '14px',
                        boxShadow: '0 0 5px rgba(255, 215, 0, 0.3)',
                        pointerEvents: 'none',
                        opacity: 0,
                        transition: 'opacity 0.2s ease'
                      }}
                    >
                      Analyze Full Game
                    </span>
                  </button>
                  {/* Gear Icon (always visible in this state, navigates correctly) */}
                </div>
              )}

            </div>

            {isEvaluating && activeTab === 'main' && !isFullAnalysisRunning && ( // Hide knight if full analysis running
              <div style={{ color: '#ffd700', fontWeight: 'bold', fontSize: '24px', fontStyle: 'italic', width: '20px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '20px' }}>
                <i className="fa-solid fa-chess-knight fa-bounce"></i>
              </div>
            )}

            {/* --- Show Progress during Full Analysis --- */}
            {isFullAnalysisRunning && (
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'flex-start', color: '#4CAF50', }}>
                <i className="fa-solid fa-chess-knight fa-spin" style={{ fontSize: '20px', marginBottom: '5px' }}></i>
                <span>{fullAnalysisProgress}%</span>
                <span>({fullAnalysisCurrentMove}/{window.moves?.length || 0})</span>
              </div>
            )}

            {/* --- End Progress --- */}
            {!isFullAnalysisRunning && ( // Hide gear during full analysis
              <i
                className="fa-solid fa-gear" // Always gear icon here
                style={{
                  cursor: 'pointer',
                  fontSize: '24px',
                  color: 'white', // Keep it white
                  transition: 'color 0.3s ease',
                  // Adjust margin based on whether loader or progress is shown
                  // marginTop: (isEvaluating && shouldRefreshUI) || isFullAnalysisRunning ? '0px' : '',
                  marginBottom: '', // Align towards vertical center
                }}
                onClick={handleTabSwitch} // Navigates to the single settings view
              ></i>
            )}

            {/* --- End Full Game Analysis Button --- */}

          </div>
        )}

        {/* --- Main Right Column / Content Area --- */}
        <div style={{
          width: '100%', // Take remaining width
          height: '100%',
          display: 'flex',
          // Center content if it's the Analyze/Review buttons
          justifyContent: (activeTab !== 'main' || isAnalyzing) ? 'flex-start' : 'center',
          alignItems: (activeTab !== 'main' || isAnalyzing) ? 'flex-start' : 'center',
        }}>
          {activeTab === 'main' ? (
            isAnalyzing ? (
              // Render Version1UI only if NOT loading in the collapsed view
              // (Loader is handled in the left column for that specific state)
              !(isEvaluating && shouldRefreshUI && uiVersion === '1' && expandedLine === null) && (
                <div style={{ width: '100%', height: '100%' }}>
                  {uiVersion === '1' ? <Version1UI /> : <Version2UI />}
                </div>
              )
            ) : (
              // Analyze/Review Buttons
              <div style={{ display: 'flex', alignItems: 'center', gap: '20px', flexDirection: 'row' }}>{/* Styles for button container */}
                <button onClick={() => handleAnalyze(false)} disabled={isEvaluating} style={{ /* ... existing styles ... */ }}> {/* Analyze button */} </button>
                <button onClick={() => {/* Full game review functionality */ }} disabled={isEvaluating} style={{ /* ... existing styles ... */ }}> {/* Review button */} </button>
              </div>
            )
          ) : (
            // Settings Content (takes full width)
            <div style={{ width: '100%' }}>
              {/* --- Settings Tab Content (Copied from before) --- */}
              <div style={{
                color: 'white',
                fontSize: '14px',
                textAlign: 'left',
                background: colors.background,
                borderRadius: '5px',
                width: '100%',
                display: 'flex',
                flexDirection: 'column', // Stack vertically
                gap: '10px',
                alignItems: 'center',
                justifyContent: 'center',
              }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  marginTop: '10px',
                  justifyContent: 'space-around', // Space out settings boxes
                  width: '100%',
                  gap: '10px', // Gap between boxes
                }}>
                  {/* Show Best Move Arrow */}
                  <div style={{ /* existing settings box style */
                    flex: '1',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    borderRadius: '5px',
                    height: '60px',
                  }}>
                    <div style={{ fontSize: '14px', marginBottom: '10px' }}><strong>Engine Arrows</strong></div>
                    {/* --- New Dropdown --- */}
                    <select
                      value={arrowSetting}
                      onChange={handleArrowSettingChange}
                      style={{
                        padding: '5px 8px',
                        paddingRight: '30px', // Add padding for the custom arrow
                        borderRadius: '4px',
                        border: '1px solid #555',
                        background: '#333',
                        color: 'white',
                        cursor: 'pointer',
                        fontSize: '14px',
                        outline: 'none',
                        // Custom arrow styling
                        appearance: 'none', // Modern browsers
                        WebkitAppearance: 'none', // Safari/Chrome
                        MozAppearance: 'none', // Firefox
                        backgroundImage: `url('data:image/svg+xml;charset=US-ASCII,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%22292.4%22%20height%3D%22292.4%22%3E%3Cpath%20fill%3D%22%23ffffff%22%20d%3D%22M287%2069.4a17.6%2017.6%200%200%200-13-5.4H18.4c-5%200-9.3%201.8-12.9%205.4A17.6%2017.6%200%200%200%200%2082.2c0%205%201.8%209.3%205.4%2012.9l128%20127.9c3.6%203.6%207.8%205.4%2012.8%205.4s9.2-1.8%2012.8-5.4L287%2095c3.5-3.5%205.4-7.8%205.4-12.8%200-5-1.9-9.2-5.5-12.8z%22%2F%3E%3C%2Fsvg%3E')`,
                        backgroundRepeat: 'no-repeat',
                        backgroundPosition: 'right 10px center',
                        backgroundSize: '10px',
                      }}
                    >
                      <option value="NONE">None</option>
                      <option value="BEST">Best Move</option>
                      <option value="ALL">All Moves</option>
                    </select>
                  </div>
                  {/* Auto-Recalculation Threshold */}
                  {/* <div style={{ 
                               flex: '1',
                               display: 'flex',
                               flexDirection: 'column',
                               alignItems: 'center',
                               padding: '10px 10px',
                               background: 'rgba(255, 255, 255, 0.05)',
                               borderRadius: '5px',
                               marginTop: '10px',

                            }}>
                             <div style={{ fontSize: '14px', marginBottom: '10px', textAlign: 'center' }}><strong>Recalculate After</strong></div>
                             <div style={{ display: 'flex', alignItems: 'center', gap: '5px', }}>
                                 <input type="number" min="1" max="8" value={recalculationThreshold} onChange={handleThresholdChange}/>
                                 <span style={{ fontWeight: 'bold' }}>moves</span>
                             </div>
                           </div> */}
                  {/* Analysis Depth */}
                  <div style={{ /* existing settings box style */
                    flex: '1',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    borderRadius: '5px',
                    height: '60px',
                  }}>
                    <div style={{ fontSize: '14px', marginBottom: '10px', textAlign: 'center' }}><strong>Analysis Depth</strong></div>
                    {/* --- Modified Input with Buttons --- */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <button
                        onClick={handleDecrementDepth}
                        disabled={analysisDepth <= 5}
                        style={{ /* Styles for '-' button */
                          padding: '3px 8px',
                          fontSize: '16px',
                          fontWeight: 'bold',
                          cursor: analysisDepth <= 5 ? 'not-allowed' : 'pointer',
                          border: '1px solid #555',
                          borderRadius: '4px',
                          background: '#444',
                          color: 'white',
                          opacity: analysisDepth <= 5 ? 0.5 : 1,
                        }}
                      >-</button>
                      <input
                        min="5"
                        max="20"
                        value={analysisDepth}
                        onChange={handleDepthChange}
                        style={{ /* Styles for number input */
                          width: '45px', // Adjust width as needed
                          textAlign: 'center',
                          padding: '4px',
                          border: '1px solid #555',
                          borderRadius: '4px',
                          background: '#333',
                          color: 'white',
                          fontSize: '14px',
                          outline: 'none',

                        }}
                      />
                      <button
                        onClick={handleIncrementDepth}
                        disabled={analysisDepth >= 20}
                        style={{ /* Styles for '+' button */
                          padding: '3px 8px',
                          fontSize: '16px',
                          fontWeight: 'bold',
                          cursor: analysisDepth >= 20 ? 'not-allowed' : 'pointer',
                          border: '1px solid #555',
                          borderRadius: '4px',
                          background: '#444',
                          color: 'white',
                          opacity: analysisDepth >= 20 ? 0.5 : 1,
                        }}
                      >+</button>

                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default Stockfish;